import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, FileArrowUp } from 'phosphor-react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { normalizarNomeUf, UF_SELECT_OPTIONS } from '../../../../constants/ufs';
import { GlassCard } from '../../../../components/glass';
import { PageBox } from '../../../../components/pageBox';
import {
  IContratoSocietario,
  IContratoSocietarioCreate,
  IFilialDoAto,
  IContratoSocioCreate,
  IDadosContratoVigente,
  IIniciarAlteracaoResponse,
  TipoAlteracao,
  TipoOperacaoSocietario,
  useSocietario,
} from '../../../../hooks/useSocietario';
import { useLegalizacao } from '../../../../hooks/useLegalizacao';
import {
  ehFluxoConstituinte,
  IDadosDaFilial,
  IWizardFormValues,
  step1Schema,
  step2Schema,
  step3Schema,
  step4Schema,
  stepContratoVigenteSchema,
  stepEmpresarioIndividualSchema,
  stepTiposAlteracaoSchema,
  wizardDefaults,
  temAdministradorEm,
  temConjuge,
} from './schema';
import { WizardStepper, IStepDef } from './components/WizardStepper';
import { CabecalhoDoWizard } from './components/CabecalhoDoWizard';
import { BarraDoWizard, EstadoDoSalvamento } from './components/BarraDoWizard';
import { houveAtualizacao, ultimaAtividade } from '../listagem';
import { StepTipoOperacao } from './components/StepTipoOperacao';
import { StepSocios } from './components/StepSocios';
import { IVigenteDoSocio } from './components/QuadroAntesDepois';
import { LIMITE as LIMITE_DO_ENDERECO } from './components/EnderecoForm';
import { ENDERECO_VAZIO, StepEmpresa } from './components/StepEmpresa';
import { StepEmpresarioIndividual } from './components/StepEmpresarioIndividual';
import { sedeNovaEmTexto, StepRevisao } from './components/StepRevisao';
import { TiposAlteracaoStep } from './components/TiposAlteracaoStep';
import { ContratoVigenteStep, avaliarAtencoesCriticos } from './components/ContratoVigenteStep';
import { StepVerificacaoEmpresa } from './components/StepVerificacaoEmpresa';
import { StepAdministracao } from './components/StepAdministracao';
import { pendenciaDaAdministracao, pessoasDaAdministracao } from './administracao';
import { normalizarOrgaoEmissor } from './orgaoEmissor';
import { computeProximoStep, indiceAlcancado } from './wizardProgress';
import { ValidacaoFidelidadePosGeracao } from '../../auditoria/components/ValidacaoFidelidadePosGeracao';
import {
  GerarContratoLoadingModal,
  GerarContratoLoadingModalTerminal,
} from './components/GerarContratoLoadingModal';
// AutoSaveIndicator removido com o auto-save (sessao 2026-05-15).
// Componente continua no codigo caso queira reabilitar no futuro.
import { PreviewDeterministicoModal } from './components/PreviewDeterministicoModal';
import { IssuesPanel } from './components/IssuesPanel';
import {
  extractApiErrorCode,
  extractApiErrorIssues,
  extractApiErrorMessage,
  extractApiErrorStatus,
} from '../../../../utils/ErrorUtils';
import { useDialog } from '../../../../providers/dialog';
import {
  atuaisDoVigente,
  cidadeDeAssinaturaComASede,
  derivarPatchesV3,
  igualAoAtual,
} from './derivarPatchesV3';
import { IEnderecoParseado, parsearEnderecoLivre } from './parsearEnderecoLivre';
import {
  cnaesDoVigente,
  ehRascunhoEditavel,
  erroPrincipal,
  errosDoYup,
  errosParaMarcar,
  mensagemDoErro,
  reverterSociosAoDesmarcar,
  salvaAoContinuar,
  temCnaePreenchido,
  textoDoDescarte,
} from './fluxoDoWizard';
import { liberacoesDoPassoSocios } from './regrasDoPassoSocios';

// Fase 6 Slice C — keys identificam steps (em vez de numeros), pois a lista
// e dinamica em alteracao.
// Pos-Fase 6 — adicionado `contrato_vigente` (so em alteracao, antes de tipos).
type StepKey =
  | 'tipo_operacao'
  | 'verificacao_empresa'
  | 'contrato_vigente'
  | 'tipos_alteracao'
  | 'empresario_individual'
  | 'socios'
  | 'administracao'
  | 'empresa'
  | 'clausulas'
  | 'revisao';

interface IStepDefKey extends Omit<IStepDef, 'key'> {
  key: StepKey;
}

const ALL_STEPS: IStepDefKey[] = [
  { key: 'tipo_operacao', label: 'Operação' },
  { key: 'verificacao_empresa', label: 'Verificação' },
  { key: 'contrato_vigente', label: 'Contrato vigente' },
  { key: 'tipos_alteracao', label: 'Tipos de alteração' },
  { key: 'empresario_individual', label: 'Empresário Individual' },
  { key: 'socios', label: 'Sócios' },
  { key: 'administracao', label: 'Administração' },
  { key: 'empresa', label: 'Empresa' },
  { key: 'clausulas', label: 'Outras cláusulas' },
  { key: 'revisao', label: 'Revisão' },
];

/**
 * Tipos de alteracao que envolvem mexer na lista de socios:
 * - `quadro_societario`: entrada/saida/cessao de quotas (socios mudam).
 * - `administracao`: trocar administrador (socio existente vira admin
 *   ou novo admin entra). Tambem precisa do step Socios.
 *
 * `capital` entrou em 2026-07-15 (vide comentario no item): aumento/reducao
 * exige redistribuir as quotas entre os socios. Endereco/razao/objeto NAO.
 *
 * Esta lista e' a fonte da verdade de "o quadro pode mudar": alem de decidir
 * se o passo Socios abre, decide a fonte do `quadro_vigente_override` no
 * /gerar. Acrescentar tipo aqui cobre os dois usos; listar a mao em um deles
 * ja causou divergencia (review do PR #626).
 */
const TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS = [
  'quadro_societario',
  // Dados de quem continua (profissao, estado civil, nome, endereco): abre o passo Socios sem
  // mexer no quadro (25/09; antes era preciso marcar "Quadro societário").
  'dados_socio',
  'administracao',
  // 'capital' (2026-07-15): aumento/redução de capital exige redistribuir as
  // quotas entre os sócios existentes (o backend barra com
  // V_CAPITAL_EXIGE_REDISTRIBUICAO / V_CAPITAL_QUOTAS_BATE_TOTAL). Habilita o
  // step de Sócios pra o operador definir as novas quotas.
  'capital',
] as const;

/**
 * Computa a lista de steps **dinamicamente** baseada no estado do form.
 *
 * - Constituicao: TipoOperacao -> Empresa -> Socios -> Revisao (4 steps, sem verificacao).
 * - Alteracao:    TipoOperacao -> TiposAlteracao -> [Socios?] -> Empresa -> Revisao
 *                 (Socios so aparece se algum tipo que afeta socios estiver
 *                 marcado — `quadro_societario` ou `administracao`).
 */
function computeSteps(
  tipoOperacao: TipoOperacaoSocietario,
  tiposAlteracao: string[] | undefined,
): IStepDefKey[] {
  if (tipoOperacao === 'constituicao') {
    // Constituicao v3: TipoOperacao → Empresa → Socios → Revisao.
    // - SEM verificacao_empresa: nova sociedade não tem cadastro/CNPJ a conferir.
    // - Empresa ANTES de Socios: capital + qtd total + valor por quota são
    //   definidos primeiro; os sócios distribuem a quantidade total de quotas.
    const ordemConstituicao = ['tipo_operacao', 'empresa', 'socios', 'revisao'];
    return ordemConstituicao
      .map(k => ALL_STEPS.find(s => s.key === k))
      .filter((s): s is IStepDefKey => s !== undefined);
  }
  if (tipoOperacao === 'transformacao') {
    // Transformação EI→LTDA: TipoOperacao → Empresário Individual → Empresa →
    // Sócios → Revisão. Espelha a constituição (a Parte B é um corpo de
    // constituição), com o passo do EI de origem antes da LTDA resultante.
    const ordemTransformacao = [
      'tipo_operacao',
      'empresario_individual',
      'empresa',
      'socios',
      'revisao',
    ];
    return ordemTransformacao
      .map(k => ALL_STEPS.find(s => s.key === k))
      .filter((s): s is IStepDefKey => s !== undefined);
  }
  // Alteracao: TipoOperacao → ContratoVigente → TiposAlteracao → [Socios?] → Empresa → Revisao
  // O step Socios so aparece pros tipos que REALMENTE mexem em socio
  // (quadro_societario/administracao/capital). Em razao/objeto/endereco os socios
  // sao continuantes: o v3 preserva a qualificacao do vigente byte-fiel e NAO roda
  // o V_SOCIO_PF_CAMPOS (camada3 fica no path v1/v2, que o v3 retorna antes de
  // atingir) — logo nao ha o que "completar" e forcar o passo so atrapalha.
  // (2026-07-21 — substitui o reveal por qualif-incompleta de 2026-07-02, que era
  // pra o backend antigo reclamar; obsoleto no v3.)
  const incluiSocios = (tiposAlteracao ?? []).some(t =>
    (TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS as readonly string[]).includes(t),
  );
  // Ordem: por padrão Sócios ANTES de Empresa. Mas quando o CAPITAL é alterado,
  // Empresa vem ANTES de Sócios (espelha a constituição): o operador define o
  // capital total + qtd de quotas no step Empresa e, na sequência, distribui
  // essas quotas entre os sócios no step Sócios. (2026-07-15)
  const capitalAlterado = (tiposAlteracao ?? []).includes('capital');
  const ordem = capitalAlterado
    ? [
        'tipo_operacao',
        'verificacao_empresa',
        'contrato_vigente',
        'tipos_alteracao',
        'empresa',
        'socios',
        'administracao',
        'clausulas',
        'revisao',
      ]
    : [
        'tipo_operacao',
        'verificacao_empresa',
        'contrato_vigente',
        'tipos_alteracao',
        'socios',
        'administracao',
        'empresa',
        'clausulas',
        'revisao',
      ];
  return (
    ordem
      .map(k => ALL_STEPS.find(s => s.key === k))
      .filter((s): s is IStepDefKey => s !== undefined)
      // Sócios só aparece se algum tipo que afeta sócios estiver marcado (ou
      // qualificação incompleta a corrigir).
      .filter(s => !(s.key === 'socios' && !incluiSocios))
      // Administração (29/09): o chat com a IA redige as cláusulas; só com o tipo marcado.
      .filter(
        s => !(s.key === 'administracao' && !(tiposAlteracao ?? []).includes('administracao')),
      )
      // Outras cláusulas (15/10): o mesmo chat, para alterar ou incluir cláusula do vigente.
      .filter(s => !(s.key === 'clausulas' && !(tiposAlteracao ?? []).includes('clausulas')))
  );
}

const toNumberOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const toStringOrNull = (v: unknown): string | null => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s || null;
};

// Linhas de sócio SEM NENHUM dado no fim da lista (a vazia do começo, a recém-adicionada) não
// vão: o back exige o nome e recusaria o rascunho inteiro (o "Salvar e sair" dos primeiros
// passos). Só as do fim: tirar outra apagaria dado — o update troca a lista inteira de sócios
// — ou desalinharia a `ordem` da cessão. Sócio com dado e sem nome vai, e o back avisa.
// Configuração da linha (valor padrão ou do botão que a criou, em qualquer nível — o
// representante também tem nacionalidade) não conta como dado; o resto conta, `id` incluso.
const CONFIG_DA_LINHA_DE_SOCIO = [
  'tipo_vinculo',
  'tipo_pessoa',
  'papel_scp',
  'nacionalidade',
  'acao',
];
export const temDadoDeSocio = (v: unknown): boolean => {
  if (v == null || v === false) return false;
  if (typeof v === 'string') return !!v.trim();
  if (Array.isArray(v)) return v.some(temDadoDeSocio);
  if (typeof v === 'object')
    return Object.entries(v as Record<string, unknown>).some(
      ([k, x]) => !CONFIG_DA_LINHA_DE_SOCIO.includes(k) && temDadoDeSocio(x),
    );
  return true;
};
const semLinhasVaziasNoFim = (socios: IWizardFormValues['socios']): IWizardFormValues['socios'] => {
  let fim = socios.length;
  while (fim > 0 && !temDadoDeSocio(socios[fim - 1])) fim -= 1;
  return socios.slice(0, fim);
};

// Representante "tem conteudo" = nome OU cpf OU endereco real preenchido. O
// scaffold vazio do RHF (`{ nome: '', cpf: '', ... }`) conta como ausente.
// Fase 2 (2026-07-16): inclui o endereco — no editar-leve do rep continuante o
// operador pode mexer SO no endereco residencial (sem tocar nome/cpf); sem isto
// `usaNested` ficava false e a edicao do rep sumia calada no payload (flat).
const representanteTemConteudo = (rep: unknown): boolean => {
  if (!rep || typeof rep !== 'object') return false;
  const r = rep as { nome?: unknown; cpf?: unknown; endereco_estruturado?: unknown };
  const end = (r.endereco_estruturado ?? {}) as { logradouro?: unknown; cep?: unknown };
  return (
    toStringOrNull(r.nome) !== null ||
    toStringOrNull(r.cpf) !== null ||
    toStringOrNull(end.logradouro) !== null ||
    toStringOrNull(end.cep) !== null
  );
};

// Representante: fonte UNICA por modo pra nunca mandar flat + nested juntos (o
// backend recusa com 422 "Payload ambiguo": 'representante.nome' e
// 'representante_nome'). Usa a sub-ficha NESTED em constituicao E na PJ ADMITIDA
// (acao=adicionar) em alteracao — ambas precisam da qualif completa do
// representante pro gate v3 (DadosPessoaisPF). Continuante PJ usa os campos flat
// legacy v2. Cobre tambem a regeracao (reabrir pelo wizard): o flat re-hidratado
// do banco colidia com o nested re-preenchido.
const montarRepresentantePayload = (
  tipoOperacao: IWizardFormValues['tipo_operacao'],
  s: IWizardFormValues['socios'][number],
) => {
  // Fase 2 (2026-07-16): continuante PJ que MARCA 'representante' (editar-leve)
  // tambem usa a sub-ficha NESTED — pra enviar o endereco residencial novo do rep
  // (endereco_estruturado -> representante_endereco_* no backend). O flat legacy so
  // carrega nome/CPF; sem o nested, a edicao do rep sumia calada no payload.
  const marcouRepresentante =
    s.acao === 'alterar' &&
    s.tipo_pessoa === 'pj' &&
    (s.tipos_alteracao_socio ?? []).includes('representante');
  const usaNested =
    (ehFluxoConstituinte(tipoOperacao) ||
      (s.acao === 'adicionar' && s.tipo_pessoa === 'pj') ||
      marcouRepresentante) &&
    representanteTemConteudo(s.representante);
  return {
    representante_nome: usaNested ? null : toStringOrNull(s.representante_nome),
    representante_cpf: usaNested ? null : toStringOrNull(s.representante_cpf),
    representante:
      usaNested && s.representante
        ? { ...s.representante, rg_orgao: normalizarOrgaoEmissor(s.representante.rg_orgao) }
        : null,
  };
};

// Constituição: objeto social derivado das descrições dos CNAEs. A cl. 3ª do
// contrato vem do loop de CNAEs no template (`código – descrição;`); a coluna
// objeto_social existe só pro validador DREI V_OBJETO_SOCIAL_PRESENTE (≥30
// chars) e NÃO é renderizada. Derivamos no persist (não num efeito de step) pra
// cobrir auto-save, create e regeração — o caminho de prod, não o conveniente.
const objetoSocialConstituicao = (cnaes: IWizardFormValues['cnaes']): string | null => {
  const txt = (cnaes ?? [])
    .map(c => c.descricao?.trim() || c.codigo?.trim() || '')
    .filter(Boolean)
    .join('; ');
  return txt || null;
};

// Sentinel `[A DEFINIR]` e convencao do backend (societario_context.py)
// pra placeholder quando nao ha valor. Extrator IA do contrato vigente
// pode emitir isso quando nao consegue ler um campo. Sem este filtro, o
// placeholder cru vira input pra IA na geracao (bug do contrato 64).
const isPlaceholderText = (v: unknown): boolean =>
  typeof v === 'string' && /^\s*\[\s*a\s+definir\s*\]\s*$/i.test(v);

// Monta a lista de sócios do form a partir do quadro vigente (acao=manter,
// quotas vigentes). Compartilhado entre o pré-fill e o reset-ao-desmarcar.
export const construirSociosDoVigente = (dados: IDadosContratoVigente) => {
  const cleanField = (v: string | null | undefined): string =>
    !v || isPlaceholderText(v) ? '' : v;
  return (dados.quadro_socios_atual ?? []).map(s => {
    const cnpjLimpo = cleanField(s.cnpj);
    // tipo_pessoa: deduzido da presenca de CNPJ apos filtro (placeholder
    // de CNPJ NAO marca o socio como PJ).
    const tipoPessoa: 'pf' | 'pj' = cnpjLimpo ? 'pj' : 'pf';
    return {
      tipo_vinculo: (s.tipo_vinculo ?? 'socio') as
        | 'socio'
        | 'socio_administrador'
        | 'administrador',
      tipo_pessoa: tipoPessoa,
      nome: cleanField(s.nome),
      cpf: cleanField(s.cpf),
      cnpj: cnpjLimpo,
      rg: cleanField(s.rg),
      rg_orgao_emissor: cleanField(s.rg_orgao_emissor),
      nacionalidade: cleanField(s.nacionalidade) || 'brasileiro',
      estado_civil: cleanField(s.estado_civil),
      regime_casamento: cleanField(s.regime_casamento),
      pacto_antenupcial: null,
      // Cônjuge: só vem quando a qualificação do vigente o cita.
      nome_conjuge: cleanField(s.nome_conjuge),
      cpf_conjuge: cleanField(s.cpf_conjuge),
      profissao: cleanField(s.profissao),
      dt_nascimento: cleanField(s.dt_nascimento),
      filiacao_pai: '',
      filiacao_mae: '',
      naturalidade: '',
      representante_nome: cleanField(s.representante_nome),
      representante_cpf: cleanField(s.representante_cpf),
      endereco: cleanField(s.endereco),
      email: cleanField(s.email),
      telefone: cleanField(s.telefone),
      quotas: s.quotas ?? null,
      // socios extraidos do vigente sao implicitamente "mantidos".
      acao: 'manter' as const,
      redistribuicao_quotas: null,
    };
  });
};

// O back só grava subcampo PREENCHIDO do `endereco_estruturado`; o vazio vai também como
// coluna nula, senão o que foi apagado (complemento, sede de tipo desmarcado) volta ao reabrir.
const SUBCAMPOS_DA_SEDE = [
  'logradouro',
  'numero',
  'complemento',
  'bairro',
  'cidade',
  'uf',
  'cep',
] as const;
const colunasVaziasDaSede = (
  sede: IWizardFormValues['endereco_estruturado'],
): Record<string, null> =>
  Object.fromEntries(
    SUBCAMPOS_DA_SEDE.filter(k => !String(sede?.[k] ?? '').trim()).map(k => [
      `endereco_${k}`,
      null,
    ]),
  );

// Rascunho de alteração de antes de 08/10: a sede nova está em TEXTO e os 7 campos, vazios.
// Entra no formulário pela mesma quebra que o patch já fazia, para o operador conferir em
// vez de redigitar. Texto igual ao do vigente não é sede nova.
export const sedeDoRascunhoAntigo = (c: {
  tipo_operacao?: string | null;
  tipos_alteracao?: string[] | null;
  endereco?: string | null;
  contrato_vigente_dados_extraidos?: { endereco_atual?: string | null } | null;
}): IEnderecoParseado | undefined => {
  const texto = (c.endereco ?? '').trim();
  const marcado = c.tipo_operacao === 'alteracao' && (c.tipos_alteracao ?? []).includes('endereco');
  if (!marcado || !texto) return undefined;
  if (igualAoAtual(texto, c.contrato_vigente_dados_extraidos?.endereco_atual)) return undefined;
  const sede = parsearEnderecoLivre(texto);
  // O texto antigo juntava sala e bloco no número: o que não cabe no campo vai ao complemento.
  if (sede.numero.length > LIMITE_DO_ENDERECO.numero) {
    sede.complemento = [sede.numero, sede.complemento].filter(Boolean).join(', ');
    sede.numero = '';
  }
  // UF que a lista não tem apareceria como "Selecione" sem erro: fica vazia, e a validação cobra.
  if (!UF_SELECT_OPTIONS.some(o => o.value === sede.uf)) sede.uf = '';
  // Campo maior que a coluna derruba o salvamento inteiro (500): este rascunho fica sem converter.
  const cabe = (Object.keys(LIMITE_DO_ENDERECO) as (keyof typeof LIMITE_DO_ENDERECO)[]).every(
    campo => sede[campo].length <= LIMITE_DO_ENDERECO[campo],
  );
  return cabe ? sede : undefined;
};

// Atividades, capital destacado e nome fantasia da filial: só vai o que foi preenchido (campo
// em branco não vira chave vazia no contrato salvo).
const comDadosDaFilial = <T extends object>(
  filial: T,
): Omit<T, keyof IDadosDaFilial> &
  Pick<IFilialDoAto, 'atividades' | 'capital_destacado' | 'nome_fantasia'> => {
  const {
    atividades,
    capital_destacado: capital,
    nome_fantasia: fantasia,
    ...resto
  } = filial as Record<string, unknown>;
  const texto = (v: unknown) => String(v ?? '').trim();
  return {
    ...resto,
    ...(texto(atividades) ? { atividades: texto(atividades) } : {}),
    ...(Number(capital) > 0 ? { capital_destacado: Number(capital) } : {}),
    ...(texto(fantasia) ? { nome_fantasia: texto(fantasia) } : {}),
  } as Omit<T, keyof IDadosDaFilial>;
};

// O inverso, para o formulário: os três campos SEMPRE presentes. Indefinido, o RHF cai nos
// valores do rascunho retomado e a filial herdava os dados de outra (review de 09/10).
const comCamposDaFilial = <T extends IDadosDaFilial>(filial: T) => ({
  ...filial,
  atividades: filial.atividades ?? '',
  capital_destacado: filial.capital_destacado ?? '',
  nome_fantasia: filial.nome_fantasia ?? '',
});

// `filiais` do contrato guarda as ABERTAS (o endereço), as ENCERRADAS (`acao: 'fechar'`, com
// NIRE e CNPJ) e as ALTERADAS (`acao: 'alterar'`, com `endereco_novo` se mudam de endereço):
// uma coluna só no back, três campos no formulário.
export const filiaisDoAto = (
  values: Pick<
    IWizardFormValues,
    'tipos_alteracao' | 'filiais' | 'filiais_fechar' | 'filiais_alterar'
  >,
): IFilialDoAto[] | null => {
  const tipos = values.tipos_alteracao ?? [];
  const abre = tipos.includes('abertura_filial');
  const fecha = tipos.includes('extincao_filial');
  const muda = tipos.includes('alteracao_filial');
  if (!abre && !fecha && !muda) return null;
  return [
    ...(abre ? (values.filiais ?? []).map(comDadosDaFilial) : []),
    ...(fecha ? (values.filiais_fechar ?? []).map(f => ({ ...f, acao: 'fechar' as const })) : []),
    ...(muda
      ? (values.filiais_alterar ?? []).map(({ novo, muda_endereco: mudaEndereco, ...f }) => ({
          ...comDadosDaFilial(f),
          acao: 'alterar' as const,
          endereco_novo: mudaEndereco === false ? null : (novo ?? null),
        }))
      : []),
  ];
};

/** O inverso, na retomada do contrato salvo. */
export const filiaisDoContrato = (
  salvas: IFilialDoAto[] | null | undefined,
): Pick<IWizardFormValues, 'filiais' | 'filiais_fechar' | 'filiais_alterar'> => ({
  filiais: (salvas ?? [])
    .filter(f => f?.acao !== 'fechar' && f?.acao !== 'alterar')
    .map(comCamposDaFilial),
  filiais_fechar: (salvas ?? [])
    .filter(f => f?.acao === 'fechar')
    .map(({ acao: _acao, endereco_novo: _novo, ...filial }) => filial),
  filiais_alterar: (salvas ?? [])
    .filter(f => f?.acao === 'alterar')
    .map(({ acao: _acao, endereco_novo: novo, ...filial }) => ({
      ...comCamposDaFilial(filial),
      novo: novo ?? { ...ENDERECO_VAZIO },
      muda_endereco: !!novo,
    })),
});

export const buildCreatePayload = (values: IWizardFormValues): IContratoSocietarioCreate => {
  return {
    tipo_operacao: values.tipo_operacao,
    template_id: toNumberOrNull(values.template_id),
    contrato_pai_id: toNumberOrNull(values.contrato_pai_id),
    empresa_id: toNumberOrNull(values.empresa_id),
    razao_social: toStringOrNull(values.razao_social),
    nome_fantasia: toStringOrNull(values.nome_fantasia),
    cnpj: toStringOrNull(values.cnpj),
    capital_social: toNumberOrNull(values.capital_social),
    // SCP: objeto texto-livre (modo 'livre') OU derivado de CNAEs (modo 'cnae').
    // LTDA/Holding constituição: sempre derivado dos CNAEs. Alteração: campo livre.
    objeto_social:
      // eslint-disable-next-line no-nested-ternary
      values.natureza_societaria === 'scp'
        ? values.dados_scp?.objeto_modo === 'cnae'
          ? objetoSocialConstituicao(values.cnaes)
          : toStringOrNull(values.objeto_social)
        : ehFluxoConstituinte(values.tipo_operacao)
          ? objetoSocialConstituicao(values.cnaes)
          : toStringOrNull(values.objeto_social),
    // Alteração sem "Objeto social" marcado: não grava CNAE no cadastro. O documento
    // não usa, e ia a sugestão da IA (vigente sem código) como se fosse do contrato
    // (varredura de 25/09). `undefined` some do JSON: o PATCH não mexe na coluna.
    cnaes: (values.tipo_operacao === 'alteracao' &&
    !(values.tipos_alteracao ?? []).includes('objeto_social')
      ? undefined
      : (values.cnaes ?? [])
    )
      ?.filter(c => c.codigo?.trim() || c.descricao?.trim())
      .map(c => ({
        codigo: c.codigo?.trim() ?? '',
        descricao: c.descricao?.trim() ?? '',
        principal: !!c.principal,
      })),
    // Alteração da sede: a coluna de texto leva a sede NOVA, montada dos 7 campos
    // (o back conta com isso em `dados_do_cadastro`).
    endereco: sedeNovaEmTexto(values) ?? toStringOrNull(values.endereco),
    foro: toStringOrNull(values.foro),
    finalidade_livre: toStringOrNull(values.finalidade_livre),
    data_inicio_atividades: toStringOrNull(values.data_inicio_atividades),
    cidade_assinatura: toStringOrNull(values.cidade_assinatura),
    junta_comercial_estado: toStringOrNull(values.junta_comercial_estado),
    junta_comercial_sigla: toStringOrNull(values.junta_comercial_sigla),
    nire: toStringOrNull(values.nire),
    // Alteracao: nulo (campo oculto; o /validar ignora nulo). Ver o pre-fill.
    data_constituicao:
      values.tipo_operacao === 'alteracao' ? null : toStringOrNull(values.data_constituicao),
    numero_alteracao: toNumberOrNull(values.numero_alteracao),
    razao_social_consolidada: toStringOrNull(values.razao_social_consolidada),
    // ============================================================
    // Schema-extension v3 elo 3 — campos empresa M3 (constituicao).
    // Sem isso, o payload do PATCH cai em lista explicita que ignora
    // tudo de v3 e o backend `_gate_v3_prerequisitos` falha no /gerar.
    // Mesma familia do bug da whitelist do backend (lista paralela ao
    // schema). Lição 2026-05-26.
    // ============================================================
    foro_cidade: toStringOrNull(values.foro_cidade),
    foro_uf: toStringOrNull(values.foro_uf),
    capital_qtd_quotas: toNumberOrNull(values.capital_qtd_quotas),
    capital_valor_quota: toNumberOrNull(values.capital_valor_quota),
    porte: (toStringOrNull(values.porte) as IContratoSocietarioCreate['porte']) ?? null,
    // Natureza só faz sentido em constituição (holding injeta cláusulas de
    // capital no render). Em alteração vai null (backend ignora).
    natureza_societaria: ehFluxoConstituinte(values.tipo_operacao)
      ? ((values.natureza_societaria ?? 'ltda') as IContratoSocietarioCreate['natureza_societaria'])
      : null,
    // Textos prontos (15/10): só a constituição os marca no formulário (na alteração vão pelo chat).
    textos_prontos: values.tipo_operacao === 'constituicao' ? (values.textos_prontos ?? []) : null,
    data_assinatura: toStringOrNull(values.data_assinatura),
    endereco_estruturado: values.endereco_estruturado ?? null,
    ...colunasVaziasDaSede(values.endereco_estruturado),
    filiais: filiaisDoAto(values),
    // Transformação EI→LTDA — snapshot do EI de origem (só nessa operação).
    dados_ei:
      values.tipo_operacao === 'transformacao' && values.dados_ei
        ? { ...values.dados_ei, rg_orgao: normalizarOrgaoEmissor(values.dados_ei.rg_orgao) }
        : null,
    // SCP — config da natureza scp (só quando scp; senão null).
    dados_scp:
      values.natureza_societaria === 'scp'
        ? ((values.dados_scp ?? null) as Record<string, unknown> | null)
        : null,
    socios: semLinhasVaziasNoFim(values.socios).map((s, idx) => ({
      // SCP: papel_scp → tipo_vinculo (ostensiva=administradora, participante=sócia)
      // p/ satisfazer o NOT NULL do banco + a regra de administrador; o engine SCP
      // roteia e particiona pelo papel_scp.
      tipo_vinculo:
        // eslint-disable-next-line no-nested-ternary
        values.natureza_societaria === 'scp'
          ? s.papel_scp === 'ostensiva'
            ? 'socio_administrador'
            : 'socio'
          : s.tipo_vinculo,
      tipo_pessoa: s.tipo_pessoa,
      papel_scp: values.natureza_societaria === 'scp' ? (s.papel_scp ?? null) : null,
      ordem: idx,
      nome: s.nome,
      cpf: toStringOrNull(s.cpf),
      rg: toStringOrNull(s.rg),
      // "SSP - GO" -> "SSP/GO" onde o operador preenche; o socio que continua vai como o vigente.
      rg_orgao_emissor: toStringOrNull(
        ehFluxoConstituinte(values.tipo_operacao) || s.acao === 'adicionar'
          ? normalizarOrgaoEmissor(s.rg_orgao_emissor)
          : s.rg_orgao_emissor,
      ),
      nacionalidade: toStringOrNull(s.nacionalidade),
      estado_civil: toStringOrNull(s.estado_civil) as IContratoSocioCreate['estado_civil'],
      regime_casamento: toStringOrNull(
        s.regime_casamento,
      ) as IContratoSocioCreate['regime_casamento'],
      pacto_antenupcial: s.pacto_antenupcial ?? null,
      // Só de quem é casado ou vive em união estável: o cônjuge digitado antes de trocar o
      // estado civil continua no formulário e sairia citado no contrato.
      nome_conjuge: temConjuge(s.estado_civil) ? toStringOrNull(s.nome_conjuge) : null,
      cpf_conjuge: temConjuge(s.estado_civil) ? toStringOrNull(s.cpf_conjuge) : null,
      profissao: toStringOrNull(s.profissao),
      dt_nascimento: toStringOrNull(s.dt_nascimento),
      filiacao_pai: toStringOrNull(s.filiacao_pai),
      filiacao_mae: toStringOrNull(s.filiacao_mae),
      naturalidade: toStringOrNull(s.naturalidade),
      cnpj: toStringOrNull(s.cnpj),
      // Flat vs nested resolvidos JUNTOS (nunca os dois) — evita 422 ambiguo.
      ...montarRepresentantePayload(values.tipo_operacao, s),
      endereco: toStringOrNull(s.endereco),
      email: toStringOrNull(s.email),
      telefone: toStringOrNull(s.telefone),
      quotas: toNumberOrNull(s.quotas),
      // Sessao 2026-05-11 (socios sync + item 15 Fase 4)
      acao: s.acao ?? null,
      // Coage IDestinoQuotaForm -> IDestinoQuota: o yup ja barrou null antes
      // do submit; aqui filtramos quaisquer placeholders residuais por seguranca.
      redistribuicao_quotas: s.redistribuicao_quotas
        ? s.redistribuicao_quotas
            .filter(d => d.socio_ordem != null && d.qtd != null)
            .map(d => ({ socio_ordem: d.socio_ordem as number, qtd: d.qtd as number }))
        : null,
      // tipos_alteracao_socio só é válido com acao=alterar (backend devolve
      // 400). Sanitiza defensivamente — protege contra resíduo de qualquer
      // fonte (retomada de rascunho, mudança de ação sem limpar o form).
      tipos_alteracao_socio: s.acao === 'alterar' ? (s.tipos_alteracao_socio ?? null) : null,
      // Porta 2 Wave 1 (2026-06-18): so faz sentido em acao=sair (sócio-admin
      // que sai mas permanece como administrador não-sócio). Fora disso, null.
      permanece_como_administrador:
        s.acao === 'sair' ? (s.permanece_como_administrador ?? false) : null,
      // ============================================================
      // Schema-extension v3 — campos socio M1+M2+M2a.
      // Backend `IContratoSocioCreate` aceita esses flat + sub-objetos
      // `endereco_estruturado` e `representante` que o validator
      // mode="before" desempacota. Mandando os flat aqui pra evitar
      // doble caminho de validacao.
      // ============================================================
      rg_via: toStringOrNull(s.rg_via),
      genero: (toStringOrNull(s.genero) as IContratoSocioCreate['genero']) ?? null,
      junta_uf: toStringOrNull(s.junta_uf),
      nire: toStringOrNull(s.nire),
      endereco_estruturado: s.endereco_estruturado ?? null,
    })),
  };
};

/**
 * PERF: isola a subscricao ao array de socios no MENOR componente possivel.
 * O botao precisa reagir ao quadro (habilita/desabilita), mas a pagina inteira
 * nao — antes a raiz observava `socios` so por causa disto e re-renderizava
 * tudo a cada tecla. Agora so este botao re-renderiza.
 */
const BotaoGerarContrato: React.FC<{
  exigeAdmin: boolean;
  isLoading: boolean;
  onClick: () => void;
}> = ({ exigeAdmin, isLoading, onClick }) => {
  const { control } = useFormContext<IWizardFormValues>();
  const socios = useWatch({ control, name: 'socios' });
  const faltaAdmin = exigeAdmin && !temAdministradorEm(socios);
  return (
    /*
      O botao NAO fica mais `isDisabled`.

      Desabilitado no Chakra vira `cursor: not-allowed` — o operador ve so' o
      🚫, clica, nada acontece, e conclui que o sistema travou. Relatado em
      prod (25/08): a operadora recarregou a pagina e gerou na 2a tentativa,
      porque o form voltou do banco ja' com o vinculo marcado.

      A explicacao SEMPRE existiu: `handleGerar` tem um toast ("Nenhum
      administrador cadastrado — volte ao passo Socios"). O `isDisabled` e' que
      impedia o clique de chegar la' — a mensagem estava inalcancavel
      justamente pra quem precisava dela. O guard do `handleGerar` continua
      barrando; so' o caminho da informacao mudou.

      `Tooltip` do Chakra no lugar do `title` nativo: o nativo so' aparece com o
      cursor parado alguns segundos, e quem clica nao espera.

      ⚠️ Sem wrapper de layout de proposito: o pai e' uma linha horizontal
      (`Flex gap={2}` sem `align`), e empilhar um aviso aqui deixaria este item
      mais alto que os irmaos. Nao da' pra validar alinhamento sem olhar a tela.
    */
    <Tooltip
      label="Marque ao menos 1 sócio como administrador no passo Sócios"
      isDisabled={!faltaAdmin}
      hasArrow
      placement="top"
    >
      <Button colorScheme="green" onClick={onClick} isLoading={isLoading}>
        Gerar contrato
      </Button>
    </Tooltip>
  );
};

// Cabeçalho azul do admin: fixo, 64px (o espaçador do Layout é `h-16`). As bolinhas
// param 8px abaixo dele ao trocar de etapa.
const TOPO_ABAIXO_DO_CABECALHO = 72;

/**
 * Cabeçalho com o título vindo do form (a razão social que o operador digita).
 * Componente próprio pelo mesmo motivo do `BotaoGerarContrato`: só ele re-renderiza
 * a cada tecla, não a página inteira.
 */
const CabecalhoComTituloDoForm: React.FC<
  Omit<React.ComponentProps<typeof CabecalhoDoWizard>, 'razaoSocial'>
> = props => {
  const { control } = useFormContext<IWizardFormValues>();
  const razaoSocial = useWatch({ control, name: 'razao_social' });
  return <CabecalhoDoWizard {...props} razaoSocial={razaoSocial ?? null} />;
};

export const SocietarioContratoCriarPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const {
    createContrato,
    updateContrato,
    gerarContrato,
    downloadContrato,
    getContrato,
    validarContrato,
    parsearVigentePreview,
    uploadDocumentoEditado,
    deleteContrato,
  } = useSocietario();
  const showDialog = useDialog();
  const { atualizarLegalizacao, obterLegalizacao } = useLegalizacao();

  const [currentStep, setCurrentStep] = useState<StepKey>('tipo_operacao');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdContrato, setCreatedContrato] = useState<IContratoSocietario | null>(null);
  // Etapa Administração: texto mexido na tela e não aprovado de novo segura o Continuar e o Gerar.
  const [administracaoEditadaNaTela, setAdministracaoEditadaNaTela] = useState(false);
  const [clausulasEditadasNaTela, setClausulasEditadasNaTela] = useState(false);
  // Estado do salvamento na barra do rodapé (28/09). `salvoEm` é a hora do último
  // salvamento que deu certo NESTA tela; na retomada, a última atividade do contrato
  // (contrato ou sócios). O `createdContrato` não é refeito depois de cada PATCH, então
  // a data dele ficava parada na hora do carregamento.
  const [salvando, setSalvando] = useState(false);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  // Issue #1158: sinaliza que os CNAEs no form vieram de SUGESTÃO IA (vigente
  // sem código, objeto em prosa) — o Step Empresa mostra um aviso pra revisar.
  const [cnaesForamSugeridos, setCnaesForamSugeridos] = useState(false);
  // Espelho sincrono do createdContrato. Necessario pro auto-save: a closure
  // de persistirRascunho rodando em setTimeout enxerga o state da captura;
  // se o usuario digita enquanto o primeiro create esta em flight, o segundo
  // trigger do auto-save le createdContrato=null e cria um SEGUNDO contrato
  // (duplicata silenciosa no banco). Atualizar o ref imediatamente apos o
  // create resolve a race.
  const createdContratoRef = React.useRef<IContratoSocietario | null>(null);
  // Descartar-ao-sair-sem-salvar (2026-06-23): distingue um contrato CRIADO
  // nesta sessão (ex: alteração em que o upload do vigente já criou o registro
  // no banco) de um CARREGADO da listagem (nunca descartar) e de um já
  // COMMITADO (save/gerar explícito). Só o primeiro, abandonado, é descartável.
  const wasLoadedRef = React.useRef(false);
  const inputRevisadoPosGeracao = React.useRef<HTMLInputElement | null>(null);
  const committedRef = React.useRef(false);
  React.useEffect(() => {
    createdContratoRef.current = createdContrato;
  }, [createdContrato]);
  // **Gating do estado pos-geracao**: o componente so renderiza
  // "Contrato pronto!" quando `geracaoOk=true`. Antes disso, o wizard fica
  // visivel mesmo que `createdContrato` ja esteja setado (retentativa
  // idempotente). Isso evita o bug em que falha de geracao deixava UI dizendo
  // "Geracao concluida" enquanto o backend tinha retornado 500.
  const [geracaoOk, setGeracaoOk] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  // Versao revisada anexada NESTA sessao da tela. `createdContrato` nao e
  // refeito depois do upload, entao sem este estado a tela mostrava o toast
  // de sucesso e continuava identica — e o "Baixar DOCX" seguia apontando
  // pra URL assinada do arquivo ANTIGO, capturada antes do envio.
  const [temRevisada, setTemRevisada] = useState(false);
  // ⚠️ O estado local não sobrevive a um reload. Sem este sync, reabrir a tela
  // de um contrato que JÁ tem versão revisada mostrava "Baixar DOCX" e escondia
  // o botão do original — o arquivo baixado continuava certo (quem resolve é o
  // backend), mas o rótulo mentia sobre qual versão viria.
  useEffect(() => {
    if (createdContrato?.contrato_editado_s3_key) setTemRevisada(true);
  }, [createdContrato?.contrato_editado_s3_key]);
  const [urlOriginal, setUrlOriginal] = useState<string | null>(null);

  // Extraido do JSX: o eslint proibe ternario aninhado, e a regra tem
  // razao aqui — sao tres estados distintos (gate pendente / revisada
  // anexada / gerado), nao dois.
  const mensagemDocumentoPronto = (() => {
    const aguardandoVistoria =
      createdContratoRef.current?.tipo_operacao === 'alteracao' && !downloadUrl;
    if (aguardandoVistoria) {
      return 'Contrato gerado. Revise a vistoria de fidelidade abaixo para liberar o download.';
    }
    if (temRevisada) {
      return 'A versão revisada que você enviou passou a ser o documento deste ato.';
    }
    return 'O DOCX foi gerado e está disponível para download.';
  })();
  // Slice 6-D — modal de loading para geracao v2 async (status='processing').
  // Substitui o redirect antigo: mantem usuario na pagina e fecha sozinho
  // quando subscribeGeracaoStatus chega em estado terminal.
  const [loadingModalContratoId, setLoadingModalContratoId] = useState<number | null>(null);
  // ⚠️ DUAS fases distintas, e confundi-las causou o pior bug desta tela:
  //   preparando  -> validacao/parse rodando, a geracao AINDA NAO comecou;
  //   acompanhando-> a geracao esta em curso e ha uma task pra assinar.
  // O modal assina o status assim que recebe um `contratoId`. Ao abri-lo na
  // fase de preparo COM o id, ele consultava o contrato e lia o estado da
  // geracao ANTERIOR (que ja estava "gerado"), concluia na hora e pintava a
  // tela de sucesso — antes mesmo do parse terminar. Na fase de preparo o
  // modal aparece SEM id, entao nao assina nada.
  const [preparandoGeracao, setPreparandoGeracao] = useState(false);
  // Pos-Fase 6: ja foi feito o PATCH advance_from_initial? Evita refazer ao
  // navegar back/next pelo step.
  const [advancedFromInitial, setAdvancedFromInitial] = useState(false);

  const tipoFromQuery = searchParams.get('tipo');
  const initialTipo: TipoOperacaoSocietario =
    tipoFromQuery === 'alteracao' ? 'alteracao' : 'constituicao';

  // Sessao 2026-05-08 (Fase D.4): retomar contrato existente. Carregado via
  // `?contratoId=` na URL — popula o form com os dados persistidos e seta o
  // step adequado ao status (rascunho_inicial → contrato_vigente; demais → empresa).
  const contratoIdFromQuery = (() => {
    const v = searchParams.get('contratoId');
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const [retomandoEdicao, setRetomandoEdicao] = useState<boolean>(contratoIdFromQuery != null);

  // `?legalizacaoId=` — handoff do board de legalização. O contrato é vinculado ao
  // processo (contrato_societario_id) assim que EXISTE, rascunho incluso (30/09): o card
  // mostra "Continuar contrato #N" e retoma daqui. Descartar o rascunho desfaz o vínculo
  // (`handleSairDoWizard`). Uma tentativa no rascunho e outra ao gerar, se a 1ª falhou.
  const legalizacaoIdFromQuery = (() => {
    const v = searchParams.get('legalizacaoId');
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const contratoCriadoId = createdContrato?.id ?? null;
  // Contrato já vinculado ao processo e a última tentativa (contrato + fase), p/ não repetir.
  const legVinculadoRef = React.useRef<number | null>(null);
  const legTentativaRef = React.useRef<string | null>(null);
  const legAvisadoRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (contratoCriadoId == null || legalizacaoIdFromQuery == null) return;
    if (legVinculadoRef.current === contratoCriadoId) return;
    const tentativa = `${contratoCriadoId}:${geracaoOk ? 'gerado' : 'rascunho'}`;
    if (legTentativaRef.current === tentativa) return;
    legTentativaRef.current = tentativa;
    // Retomado pelo "Continuar contrato" do card: o processo já apontava para ele. Se agora
    // não aponta, alguém trocou ou desvinculou pelo card — um F5 aqui não desfaz isso.
    const retomado = contratoIdFromQuery === contratoCriadoId;
    const vincular = async () => {
      // Só liga processo livre (ou já neste contrato), e só o contrato criado nesta página.
      const { data: processo } = await obterLegalizacao(legalizacaoIdFromQuery);
      const atual = processo?.contrato_societario_id ?? null;
      if (atual === contratoCriadoId) {
        legVinculadoRef.current = contratoCriadoId;
        return;
      }
      if (atual != null && legAvisadoRef.current !== contratoCriadoId) {
        legAvisadoRef.current = contratoCriadoId;
        toast({
          status: 'warning',
          title: `O processo já está com o contrato #${atual}`,
          description: 'Este contrato não foi vinculado. Para trocar, use o card da legalização.',
        });
      }
      if (atual != null || retomado) return;
      // Só o vínculo do contrato. A rota (A/B) é definida ANTES, na etapa
      // "Definição + Rota" do board (o operador marca os tipos), não na geração.
      await atualizarLegalizacao(legalizacaoIdFromQuery, {
        contrato_societario_id: contratoCriadoId,
      });
      legVinculadoRef.current = contratoCriadoId;
    };
    vincular().catch(err => {
      // Antes falhava calado: o card seguia "Gerar" e um 2º clique criava outro contrato.
      toast({
        status: 'warning',
        title: 'Contrato não vinculado ao processo',
        description: `${extractApiErrorMessage(err)} Vincule pelo card da legalização.`,
      });
    });
  }, [
    contratoCriadoId,
    contratoIdFromQuery,
    geracaoOk,
    legalizacaoIdFromQuery,
    atualizarLegalizacao,
    obterLegalizacao,
    toast,
  ]);

  const formMethods = useForm<IWizardFormValues>({
    defaultValues: {
      ...wizardDefaults,
      tipo_operacao: initialTipo,
      // Constituição: o 1º sócio já entra como Sócio-administrador (toda LTDA
      // precisa de administrador; evita o operador esquecer e tomar o gate
      // "≥1 admin"). Em alteração mantém o default 'socio'.
      socios:
        initialTipo === 'constituicao'
          ? [{ ...wizardDefaults.socios[0], tipo_vinculo: 'socio_administrador' }]
          : wizardDefaults.socios,
    },
    mode: 'onChange',
  });

  // PERF 2026-08-06: NAO observar `socios` aqui. Este componente e a raiz do
  // wizard — observar o array inteiro fazia CADA TECLA em qualquer campo de
  // qualquer socio re-renderizar a pagina toda, e o StepSocios junto. Medido
  // com 13 socios: ~305ms por render, DOIS renders por tecla (um da raiz, um da
  // subscricao propria do StepSocios) = ~600ms de travamento por tecla.
  // O unico consumo era derivar `temAdministrador` (um booleano de 3 campos):
  //   - no `handleGerar` -> le com getValues(), sem subscricao;
  //   - no botao Gerar   -> `BotaoGerarContrato`, que observa por conta propria
  //                          e re-renderiza SO ele.
  const tipoOperacaoWatched = useWatch({
    control: formMethods.control,
    name: 'tipo_operacao',
  });
  const tiposAlteracaoWatched = useWatch({
    control: formMethods.control,
    name: 'tipos_alteracao',
  });
  // Lido no render: sem a leitura o RHF não calcula o isDirty. Re-renderiza a raiz só quando
  // ele vira, não a cada tecla. Usado pelo sair do wizard em contrato que não salva sozinho.
  const { isDirty: formAlterado } = formMethods.formState;
  // O formulário do último PATCH que deu certo (JSON): o que já foi gravado não é "sem salvar".
  const formGravadoRef = React.useRef<string | null>(null);
  // Snapshot dos tipos no render anterior — usado pra detectar tipos
  // DESMARCADOS e restaurar os campos correspondentes ao vigente.
  const prevTiposRef = useRef<TipoAlteracao[]>([]);
  // Exclui acao=sair: um admin que esta saindo NAO conta como administrador
  // remanescente. Alinha com o banner do StepSocios — sem isto, o unico admin
  // marcado 'sair' passava o handleGerar e gerava contrato sem administrador.
  // Porta 2 Wave 1 (2026-06-18): EXCETO quem sai mas PERMANECE como
  // administrador (nao-socio) — esse continua administrando, entao satisfaz o
  // "≥1 admin" (senao o caso dominante da Porta 2 travava o Gerar).

  // Pos-Fase 6: levantamos `exigeAdmin` do handleGerar pro nivel do componente
  // pra usar tambem no `isDisabled` do botao Gerar. Sem isso, o botao ficava
  // sempre travado em alteracao que nao mexe em socios — bug reportado pelo
  // usuario antes da feature de contrato vigente inline.
  const alteracaoMexeEmSociosWatched = (tiposAlteracaoWatched ?? []).some(t =>
    (TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS as readonly string[]).includes(t),
  );
  const exigeAdmin = ehFluxoConstituinte(tipoOperacaoWatched) || alteracaoMexeEmSociosWatched;

  // Lista de steps recalculada a cada render — depende de tipo_operacao e
  // tipos_alteracao marcados. Em alteracao com quadro_societario nao marcado,
  // o step 'socios' some.
  const stepsList = computeSteps(tipoOperacaoWatched, tiposAlteracaoWatched);
  const currentIdx = Math.max(
    0,
    stepsList.findIndex(s => s.key === currentStep),
  );

  // Etapas já alcançadas (28/09): as bolinhas deixam ir para a frente até a mais
  // adiantada — quem voltou da Revisão para Sócios volta direto para a Revisão. O
  // caminho é validado como no Continuar (`irParaEtapa`).
  const [etapasAlcancadas, setEtapasAlcancadas] = useState<Set<string>>(
    () => new Set(['tipo_operacao']),
  );
  useEffect(() => {
    setEtapasAlcancadas(atual =>
      atual.has(currentStep) ? atual : new Set(atual).add(currentStep),
    );
  }, [currentStep]);
  const alcancadoIdx = indiceAlcancado(
    stepsList.map(etapa => String(etapa.key)),
    etapasAlcancadas,
    currentIdx,
  );

  const validateStep = async (stepKey: StepKey): Promise<boolean> => {
    // Com o tipo Administração marcado, o chat é obrigatório (decisão do user, 29/09): a etapa só
    // passa com a redação aprovada, e aprovada como está na tela.
    if (stepKey === 'administracao') {
      const pendencia = pendenciaDaAdministracao(
        createdContrato?.administracao_ia,
        administracaoEditadaNaTela,
        // Quem administra agora, pelo passo Sócios: mudou depois da aprovação, aprova de novo.
        pessoasDaAdministracao(
          formMethods.getValues('socios'),
          createdContrato?.contrato_vigente_dados_extraidos,
        ),
      );
      if (pendencia) {
        toast({
          status: 'warning',
          title: 'Aprove a redação da administração',
          description: pendencia,
        });
        return false;
      }
      return true;
    }
    // Outras cláusulas (15/10): idem, sem quem administra.
    if (stepKey === 'clausulas') {
      const pendencia = pendenciaDaAdministracao(
        createdContrato?.clausulas_ia,
        clausulasEditadasNaTela,
        undefined,
        'clausulas',
      );
      if (pendencia) {
        toast({
          status: 'warning',
          title: 'Aprove a redação das cláusulas',
          description: pendencia,
        });
        return false;
      }
      return true;
    }
    const values = formMethods.getValues();
    // O erro de antes some: o do campo o RHF limpa ao digitar (mode onChange), mas o da
    // lista (ex.: "socios") só sai aqui.
    formMethods.clearErrors();
    // socioSchema usa `$tipo_operacao` em `.when()` pra decidir entre validar
    // `endereco` legacy (alteracao v1/v2) ou `endereco_estruturado` (constituicao
    // v3). Sem o context, ambos os ramos caem no `nullable()` e a UX vira
    // gate backend silencioso — bug do Revisão "Endereço é obrigatório"
    // mesmo com EnderecoForm preenchido tinha origem AQUI.
    // natureza_societaria vai no context p/ o socioSchema (`$natureza_societaria`
    // relaxa Junta da PJ na SCP) e p/ a regra de papéis do step2 (ostensiva/participante).
    // Quotas de cada sócio no vigente, por CPF/CNPJ: a cessão não passa do que o cedente tem.
    const quotasVigentePorDoc: Record<string, number> = {};
    (createdContrato?.contrato_vigente_dados_extraidos?.quadro_socios_atual ?? []).forEach(s => {
      const doc = String(s?.cpf || s?.cnpj || '').replace(/\D/g, '');
      if (doc && s?.quotas != null) quotasVigentePorDoc[doc] = Number(s.quotas);
    });
    const context = {
      tipo_operacao: values.tipo_operacao,
      natureza_societaria: values.natureza_societaria,
      // Fase 0: o gate `cadastro_confere` só é obrigatório quando o wizard veio da
      // Legalização (`?legalizacaoId=`); alteração avulsa não trava.
      ehFluxoLegalizacao: legalizacaoIdFromQuery != null,
      quotasVigentePorDoc,
    };
    try {
      if (stepKey === 'tipo_operacao') {
        await step1Schema.validate(values, { abortEarly: false, context });
      } else if (stepKey === 'empresario_individual') {
        await stepEmpresarioIndividualSchema.validate(values, { abortEarly: false, context });
      } else if (stepKey === 'contrato_vigente') {
        await stepContratoVigenteSchema.validate(values, { abortEarly: false, context });
      } else if (stepKey === 'tipos_alteracao') {
        await stepTiposAlteracaoSchema.validate(values, { abortEarly: false, context });
      } else if (stepKey === 'socios') {
        await step2Schema.validate(values, { abortEarly: false, context });
      } else if (stepKey === 'empresa') {
        await step3Schema.validate(values, { abortEarly: false, context });
      } else if (stepKey === 'revisao') {
        await step4Schema.validate(values, { abortEarly: false, context });
      }
      return true;
    } catch (err: any) {
      // Sem resolver no useForm, o erro do yup não chegava ao formState: o campo não
      // ficava vermelho e o card do sócio com erro não abria (review do PR #772).
      const erros = errosDoYup(err);
      // Um erro por campo, o do toast na frente. O toast mostra a causa: a cessão, e não o
      // "Quotas deve ser maior que zero" de quem cede demais (a consequência).
      errosParaMarcar(erros).forEach(e =>
        formMethods.setError(e.caminho as any, { type: 'manual', message: e.mensagem }),
      );
      toast({
        status: 'error',
        title: 'Revise os dados',
        description: mensagemDoErro(erroPrincipal(erros), values.socios),
      });
      return false;
    }
  };

  /**
   * Pos-Fase 6: aplica os dados extraidos do contrato vigente nos campos do
   * form. Mapeia `IDadosContratoVigente` (snapshot da Fase 2.5) → campos do
   * IWizardFormValues. Em MVP (Sessao 2), sobrescreve sem confirmacao —
   * Sessao 3 vai adicionar metadata "extraido vs editado" + dialog de
   * confirmacao em re-upload.
   *
   * Mapeamento:
   * - razao_social, cnpj, endereco_atual, objeto_social_atual, nire vao
   *   diretos pros campos do Step Empresa.
   * - capital_social_atual → capital_social.
   * - numero_ultima_alteracao + 1 → numero_alteracao (proxima alteracao).
   * - quadro_socios_atual → socios (1 entrada PF/PJ por socio com
   *   quotas/percentual; campos restantes ficam NULL pra usuario completar).
   */
  const aplicarPreFillDoVigente = (dados: IDadosContratoVigente) => {
    const setF = formMethods.setValue;
    // Helper: so seta o campo se o valor extraido NAO for sentinel/placeholder.
    // Trata valor literal '[A DEFINIR]' como ausencia de dado — operador
    // preenche manualmente (form forca via .required em alteracao).
    const setIfReal = (campo: keyof IWizardFormValues, valor: string | null | undefined): void => {
      if (!valor || isPlaceholderText(valor)) return;
      setF(campo as never, valor as never, { shouldDirty: true });
    };

    setIfReal('razao_social', dados.razao_social);
    setIfReal('nome_fantasia', dados.nome_fantasia);
    setIfReal('cnpj', dados.cnpj);
    setIfReal('nire', dados.nire);
    setIfReal('endereco', dados.endereco_atual);
    setIfReal('objeto_social', dados.objeto_social_atual);
    // Bloco 13 (2026-06-03): extrator IA agora cobre foro, cidade_assinatura,
    // junta_comercial_*, data_inicio_atividades. Antes ficavam vazios e
    // operador preenchia a mao.
    setIfReal('foro', dados.foro);
    setIfReal('cidade_assinatura', dados.cidade_assinatura);
    // normaliza pro `nome` canônico (acento) — senão "Goias" (a IA devolve sem
    // acento) não casa a option "Goiás" do dropdown e o campo aparece vazio.
    setIfReal('junta_comercial_estado', normalizarNomeUf(dados.junta_comercial_estado));
    setIfReal('junta_comercial_sigla', dados.junta_comercial_sigla);
    setIfReal('data_inicio_atividades', dados.data_inicio_atividades);
    // Auto-popular `razao_social_consolidada` com a razao atual — operador
    // edita se a alteracao mudar a razao. Espelhamento defensivo: campo
    // required em alteracao que ficava sempre vazio na extracao (nao
    // existe no vigente como tal — e a "razao depois da alteracao").
    setIfReal('razao_social_consolidada', dados.razao_social);
    // data_constituicao nao e' pre-preenchida (25/09): o campo saiu da alteracao em
    // 08/06, e a data lida errada (no futuro) barrava o /validar sem contorno.
    if (dados.capital_social_atual != null) {
      setF('capital_social', dados.capital_social_atual, { shouldDirty: true });
    }
    if (dados.numero_ultima_alteracao != null) {
      setF('numero_alteracao', dados.numero_ultima_alteracao + 1, { shouldDirty: true });
    }
    // Bloco 13 (2026-06-03): CNAEs estruturados — primeiro CNAE da lista
    // marcado como principal por convencao (operador troca se quiser).
    // Issue #1158: vigente sem código CNAE (objeto em prosa) — o back sugeriu
    // CNAEs a partir da prosa via IA; o Step Empresa pede para revisar.
    const cnaesLidos = cnaesDoVigente(dados);
    if (cnaesLidos.cnaes.length > 0) {
      setF('cnaes', cnaesLidos.cnaes, { shouldDirty: true });
      setCnaesForamSugeridos(cnaesLidos.sugeridos);
    }
    // Socios: reusa o helper compartilhado (acao=manter, quotas vigentes).
    if (dados.quadro_socios_atual && dados.quadro_socios_atual.length > 0) {
      const sociosDoVigente = construirSociosDoVigente(dados);
      setF('socios', sociosDoVigente, { shouldDirty: true });
    }
  };

  // Restaura os campos de UM tipo de alteração para os valores do vigente.
  // Usado pelo efeito de reset-ao-desmarcar abaixo.
  const resetTipoParaVigente = (tipo: TipoAlteracao, dados: IDadosContratoVigente): void => {
    const setF = formMethods.setValue;
    const opts = { shouldDirty: false } as const;
    switch (tipo) {
      case 'razao_social':
        setF('razao_social', (dados.razao_social ?? '') as never, opts);
        setF('razao_social_consolidada', (dados.razao_social ?? '') as never, opts);
        break;
      // `nome_fantasia` saiu do case acima: desmarcar "Razão social" nao pode
      // mais resetar a fantasia, senao quem esta' alterando SO' a fantasia
      // perde o valor digitado ao mexer no outro checkbox.
      case 'nome_fantasia':
        setF('nome_fantasia', (dados.nome_fantasia ?? '') as never, opts);
        break;
      // Os sócios de capital, quadro, dados de sócio e administração voltam juntos, pelo
      // que os tipos restantes liberam (reverterSociosDoPasso, no efeito abaixo).
      case 'capital':
        setF('capital_social', (dados.capital_social_atual ?? null) as never, opts);
        setF('capital_qtd_quotas', null as never, opts);
        setF('capital_valor_quota', null as never, opts);
        break;
      case 'endereco': {
        // A cidade de assinatura que seguia a sede nova volta com ela.
        const assinatura = cidadeDeAssinaturaComASede(
          formMethods.getValues('cidade_assinatura'),
          '',
          formMethods.getValues('endereco_estruturado.cidade'),
          dados.endereco_atual,
          dados.cidade_assinatura,
        );
        if (assinatura != null) setF('cidade_assinatura', assinatura as never, opts);
        setF('endereco', (dados.endereco_atual ?? '') as never, opts);
        setF('endereco_estruturado', { ...wizardDefaults.endereco_estruturado } as never, opts);
        break;
      }
      case 'abertura_filial':
        setF('filiais', [] as never, opts);
        break;
      case 'extincao_filial':
        setF('filiais_fechar', [] as never, opts);
        break;
      case 'alteracao_filial':
        setF('filiais_alterar', [] as never, opts);
        break;
      case 'objeto_social':
        setF('objeto_social', (dados.objeto_social_atual ?? '') as never, opts);
        setF(
          'cnaes',
          (dados.cnaes_atual ?? []).map((c, i) => ({
            codigo: c.codigo,
            descricao: c.descricao,
            principal: i === 0,
          })) as never,
          opts,
        );
        break;
      default:
        break;
    }
  };

  // Desmarcou um tipo que abria o passo Sócios. Antes só havia reset quando nenhum tipo de
  // sócio sobrava: com "Capital" ou "Dados de sócio" ainda marcados, cessão, saída e entrada
  // ficavam no payload (review do PR #772). Agora volta ao vigente, sócio a sócio, o que os
  // tipos restantes não liberam; sem nada liberado, o quadro inteiro.
  const reverterSociosDoPasso = (dados: IDadosContratoVigente): void => {
    if ((dados.quadro_socios_atual ?? []).length === 0) return;
    const liberacoes = liberacoesDoPassoSocios(formMethods.getValues('tipos_alteracao'));
    const doVigente = construirSociosDoVigente(dados);
    const socios = Object.values(liberacoes).some(Boolean)
      ? reverterSociosAoDesmarcar(formMethods.getValues('socios') ?? [], doVigente, liberacoes)
      : doVigente;
    formMethods.setValue('socios', socios as never, { shouldDirty: false });
  };

  // Marcou "Objeto social" com o seletor vazio (a alteração sem esse tipo não grava CNAE):
  // parte dos CNAEs do vigente, senão o objeto novo saía só com o CNAE digitado.
  const preencherCnaesDoVigenteSeVazio = (dados: IDadosContratoVigente): void => {
    if (temCnaePreenchido(formMethods.getValues('cnaes'))) return;
    const doVigente = cnaesDoVigente(dados);
    if (doVigente.cnaes.length === 0) return;
    formMethods.setValue('cnaes', doVigente.cnaes, { shouldDirty: true });
    setCnaesForamSugeridos(doVigente.sugeridos);
  };

  // Bug do teste manual (2026-06-10): desmarcar um tipo de alteração não
  // limpava as edições feitas naquele tipo — elas persistiam no form e iam
  // pro payload (sócios/override/capital). Ao detectar um tipo REMOVIDO,
  // restaura os campos dele para os valores do vigente (snapshot extraído).
  useEffect(() => {
    const atual = (tiposAlteracaoWatched ?? []) as TipoAlteracao[];
    const anteriores = prevTiposRef.current;
    prevTiposRef.current = atual;
    const dados = createdContrato?.contrato_vigente_dados_extraidos;
    if (!dados) return;
    if (atual.includes('objeto_social') && !anteriores.includes('objeto_social')) {
      preencherCnaesDoVigenteSeVazio(dados);
    }
    const removidos = anteriores.filter(t => !atual.includes(t));
    if (removidos.length === 0) return;
    removidos.forEach(tipo => resetTipoParaVigente(tipo, dados));
    const afetaSocios = (t: string) =>
      (TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS as readonly string[]).includes(t);
    if (removidos.some(afetaSocios)) reverterSociosDoPasso(dados);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiposAlteracaoWatched]);

  /**
   * Pos-Fase 6: callback do ContratoVigenteStep apos upload bem-sucedido.
   * Aplica pre-fill imediatamente (Sessao 2 MVP) e cria stub local de
   * `createdContrato` pra que `handleGerar` saiba pular o create
   * (contrato ja existe no backend em rascunho_inicial).
   */
  const handleVigenteUploadSuccess = (resp: IIniciarAlteracaoResponse) => {
    // Sessao 2026-05-15 (review final P0 A1): incluir
    // `contrato_vigente_dados_extraidos` no stub pra que o primeiro
    // `handleGerar` apos o upload exercite o check de avisos criticos do
    // Matias #8 (linha ~980). Sem este campo, o branch
    // `if (alteracao && contrato.contrato_vigente_dados_extraidos)` era
    // FALSO no primeiro gerar — operador queimava IA com extracao critica.
    setCreatedContrato({
      id: resp.contrato_id,
      tipo_operacao: 'alteracao',
      status: 'rascunho_inicial',
      socios: [],
      contrato_vigente_dados_extraidos: resp.dados_extraidos ?? null,
    } as IContratoSocietario);
    setAdvancedFromInitial(false);

    if (resp.dados_extraidos) {
      aplicarPreFillDoVigente(resp.dados_extraidos);
    }
  };

  const updateMutation = useMutation({ mutationFn: updateContrato });

  // Sessao 2026-05-08 (Fase D.4): ao montar, se ?contratoId= veio na URL,
  // carrega o contrato persistido, popula o form e ajusta o step inicial.
  // Ignora se ja temos createdContrato (idempotencia caso o usuario navegue
  // entre steps sem perder o contexto).
  useEffect(() => {
    let cancelled = false;
    if (!retomandoEdicao || createdContrato || contratoIdFromQuery == null) {
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const res = await getContrato({
          queryKey: ['contrato', contratoIdFromQuery],
        } as any);
        if (cancelled) return;
        const c = res.data;
        if (!c) {
          toast({
            status: 'error',
            title: 'Contrato não encontrado',
            description: `ID #${contratoIdFromQuery} não existe ou foi removido.`,
          });
          setRetomandoEdicao(false);
          return;
        }
        // Alteração sem "Objeto social" não grava CNAE (o PATCH omite a coluna): a retomada
        // parte dos CNAEs do vigente, senão o seletor abria vazio ao marcar o tipo depois e
        // o objeto novo perdia as atividades antigas (review do PR #772).
        const cnaesRetomados =
          c.tipo_operacao === 'alteracao' && !temCnaePreenchido(c.cnaes)
            ? cnaesDoVigente(c.contrato_vigente_dados_extraidos)
            : { cnaes: (c.cnaes ?? []) as IWizardFormValues['cnaes'], sugeridos: false };
        // Reseta o form com os dados do contrato persistido. Mantem
        // wizardDefaults pra campos nao retornados pelo backend.
        formMethods.reset({
          ...wizardDefaults,
          tipo_operacao: c.tipo_operacao,
          template_id: (c as any).template_id ?? null,
          contrato_pai_id: (c as any).contrato_pai_id ?? null,
          empresa_id: c.empresa_id ?? null,
          razao_social: c.razao_social ?? '',
          nome_fantasia: c.nome_fantasia ?? '',
          cnpj: c.cnpj ?? '',
          capital_social: toNumberOrNull(c.capital_social),
          objeto_social: c.objeto_social ?? '',
          cnaes: cnaesRetomados.cnaes,
          endereco: c.endereco ?? '',
          foro: c.foro ?? '',
          finalidade_livre: c.finalidade_livre ?? '',
          data_inicio_atividades: c.data_inicio_atividades ?? '',
          cidade_assinatura: c.cidade_assinatura ?? '',
          junta_comercial_estado: c.junta_comercial_estado ?? '',
          junta_comercial_sigla: c.junta_comercial_sigla ?? '',
          nire: c.nire ?? '',
          data_constituicao: c.data_constituicao ?? '',
          numero_alteracao: c.numero_alteracao ?? null,
          razao_social_consolidada: c.razao_social_consolidada ?? '',
          tipos_alteracao: ((c as any).tipos_alteracao ?? []) as any,
          ...filiaisDoContrato((c as any).filiais),
          contrato_id_inicial: (c as any).contrato_vigente_s3_key ? c.id : null,
          // Regeracao: re-hidratar os campos v3 da EMPRESA que o backend
          // persiste e devolve na response. Sem isto, reabrir pelo wizard os
          // mandava como null no PATCH seguinte e ZERAVA foro/capital/porte/
          // data + endereco estruturado no banco -> o /gerar batia no gate
          // _gate_v3_prerequisitos (422). Espelha o fix do endereco do socio.
          foro_cidade: (c as any).foro_cidade ?? null,
          foro_uf: (c as any).foro_uf ?? null,
          capital_qtd_quotas: (c as any).capital_qtd_quotas ?? null,
          capital_valor_quota: toNumberOrNull((c as any).capital_valor_quota),
          porte: ((c as any).porte ?? null) as IWizardFormValues['porte'],
          natureza_societaria: ((c as any).natureza_societaria ??
            'ltda') as IWizardFormValues['natureza_societaria'],
          textos_prontos: (c as any).textos_prontos ?? [],
          data_assinatura: (c as any).data_assinatura ?? null,
          endereco_estruturado:
            (c as any).endereco_estruturado ??
            sedeDoRascunhoAntigo(c as any) ??
            wizardDefaults.endereco_estruturado,
          // Transformação: re-hidrata o `dados_ei` (JSONB do EI) na reedição. Sem
          // isto, reabrir o contrato deixava a aba Empresário Individual vazia e o
          // PATCH (REPLACE-ALL) zerava o dados_ei no banco. Mescla com os defaults
          // pra garantir os nested (endereço/sede) como objeto não-null (RHF v7).
          dados_ei: c.dados_ei
            ? {
                ...wizardDefaults.dados_ei,
                ...c.dados_ei,
                endereco_estruturado: {
                  ...((wizardDefaults.dados_ei as any)?.endereco_estruturado ?? {}),
                  ...(c.dados_ei.endereco_estruturado ?? {}),
                },
                sede_estruturada: {
                  ...((wizardDefaults.dados_ei as any)?.sede_estruturada ?? {}),
                  ...(c.dados_ei.sede_estruturada ?? {}),
                },
              }
            : wizardDefaults.dados_ei,
          // SCP — re-hidrata o config `dados_scp` (JSONB) na reedição, mesclando
          // com os defaults p/ garantir todas as flags presentes no form.
          dados_scp: (c as any).dados_scp
            ? { ...wizardDefaults.dados_scp, ...((c as any).dados_scp as object) }
            : wizardDefaults.dados_scp,
          socios:
            c.socios && c.socios.length > 0
              ? (c.socios.map(s => ({
                  tipo_vinculo: s.tipo_vinculo,
                  tipo_pessoa: s.tipo_pessoa,
                  papel_scp: (s as any).papel_scp ?? null,
                  nome: s.nome,
                  cpf: s.cpf ?? '',
                  rg: s.rg ?? '',
                  rg_orgao_emissor: s.rg_orgao_emissor ?? '',
                  nacionalidade: s.nacionalidade ?? 'brasileiro',
                  estado_civil: s.estado_civil ?? '',
                  regime_casamento: s.regime_casamento ?? '',
                  // Gênero persistido — sem ele, reabrir o contrato perdia o
                  // gênero e a regeneração/concordância da qualif do
                  // continuante falhava (2026-06-11).
                  genero: (s as any).genero ?? null,
                  pacto_antenupcial: s.pacto_antenupcial ?? null,
                  nome_conjuge: s.nome_conjuge ?? '',
                  cpf_conjuge: s.cpf_conjuge ?? '',
                  profissao: s.profissao ?? '',
                  dt_nascimento: s.dt_nascimento ?? '',
                  filiacao_pai: s.filiacao_pai ?? '',
                  filiacao_mae: s.filiacao_mae ?? '',
                  naturalidade: s.naturalidade ?? '',
                  cnpj: s.cnpj ?? '',
                  representante_nome: s.representante_nome ?? '',
                  representante_cpf: s.representante_cpf ?? '',
                  // Regeracao: re-hidratar a sub-ficha NESTED do representante
                  // (o backend monta `s.representante` computado dos flats) +
                  // rg_via/junta_uf/nire. Sem isto, reabrir constituicao PJ
                  // mostrava o representante em branco e o PATCH (REPLACE-ALL)
                  // zerava rg/estado_civil/dt_nascimento/profissao/genero/
                  // endereco do representante no banco.
                  representante: (s as any).representante
                    ? {
                        ...(wizardDefaults.socios[0].representante ?? {}),
                        ...(s as any).representante,
                        endereco_estruturado:
                          (s as any).representante.endereco_estruturado ??
                          (wizardDefaults.socios[0].representante as any)?.endereco_estruturado ??
                          null,
                      }
                    : { ...(wizardDefaults.socios[0].representante ?? {}) },
                  rg_via: (s as any).rg_via ?? null,
                  junta_uf: (s as any).junta_uf ?? null,
                  nire: (s as any).nire ?? null,
                  endereco: s.endereco ?? '',
                  // Re-hidrata o endereço ESTRUTURADO na retomada/re-edição (o
                  // backend retorna `endereco_estruturado` computado dos campos
                  // flat persistidos). Sem isto, reabrir o contrato perdia o
                  // estruturado → auto-save gravava vazio → gate "falta
                  // endereço" ao regerar (incidente contrato 2487, 2026-06-11).
                  endereco_estruturado: (s as any).endereco_estruturado ?? null,
                  email: s.email ?? '',
                  telefone: s.telefone ?? '',
                  quotas: s.quotas ?? null,
                  // Sessao 2026-05-11 (socios sync) — pre-popula manter
                  // pra retomada zero-touch. Operador so toca onde muda.
                  acao: (s as any).acao ?? (c.tipo_operacao === 'alteracao' ? 'manter' : null),
                  redistribuicao_quotas: (s as any).redistribuicao_quotas ?? null,
                  // Sessao 2026-05-15 (review final P0 A7): campo persistido
                  // no backend (Fase 4 Slice E) mas estava sendo omitido na
                  // retomada. Sem ele, operador B abrindo rascunho do A com
                  // `acao=alterar` recebia erro Yup "marque pelo menos 1
                  // aspecto" mesmo sem ter tocado em nada.
                  tipos_alteracao_socio: (s as any).tipos_alteracao_socio ?? null,
                  // Porta 2 Wave 1 (2026-06-18): re-hidrata o flag na retomada.
                  permanece_como_administrador: (s as any).permanece_como_administrador ?? false,
                })) as any)
              : [{ ...wizardDefaults.socios[0] }],
        });
        setCreatedContrato(c);
        setCnaesForamSugeridos(cnaesRetomados.sugeridos);
        // Contrato CARREGADO de um existente — nunca descartar ao sair.
        wasLoadedRef.current = true;
        // Pos-rascunho_inicial nao precisa re-PATCH advance_from_initial.
        setAdvancedFromInitial(c.status !== 'rascunho_inicial');
        // Sessao 2026-05-08 (contrato 73): step inicial pelo primeiro campo
        // faltante. Logica compartilhada com `ContratoSocietarioCard`
        // (badge "Próximo: <Step>" na listagem). `computeProximoStep`
        // pode devolver 'gerado' (estado terminal) — caimos em 'revisao'.
        const proximo = computeProximoStep(c);
        const step: StepKey = proximo && proximo !== 'gerado' ? proximo : 'revisao';
        setCurrentStep(step);
        // Na retomada, o operador já tinha chegado até esta etapa.
        const etapasDoContrato = computeSteps(c.tipo_operacao, c.tipos_alteracao ?? undefined);
        const ateIdx = Math.max(
          0,
          etapasDoContrato.findIndex(e => e.key === step),
        );
        setEtapasAlcancadas(new Set(etapasDoContrato.slice(0, ateIdx + 1).map(e => String(e.key))));
        const ultima = ultimaAtividade(c);
        setSalvoEm(ultima && houveAtualizacao(c.dt_criacao, ultima) ? new Date(ultima) : null);
        toast({
          status: 'info',
          title: 'Retomando contrato',
          description: `Contrato #${c.id} carregado. Continue de onde parou.`,
          duration: 4000,
        });
      } catch (err: any) {
        if (cancelled) return;
        toast({
          status: 'error',
          title: 'Falha ao carregar contrato',
          description: extractApiErrorMessage(err),
        });
      } finally {
        if (!cancelled) setRetomandoEdicao(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoIdFromQuery]);

  // Sessao 2026-05-08 (Fase D.2): salva o estado atual em qualquer etapa
  // do wizard sem validacao bloqueante, marca status='rascunho' e volta
  // pra listagem. Outros usuarios do departamento podem continuar via
  // notificacao cross-user (Fase D.3) — backend dispara via update.
  // Sessao 2026-05-11 (item 6 Fase 3): a logica de persistencia foi
  // extraida em `persistirRascunho` pra ser reusada pelo auto-save
  // silencioso (sem toast, sem navegacao).
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  // Mutex: evita 2 saves simultaneos. React Hook Form pode disparar dirty
  // em sequencia rapida; o segundo save fica em standby ate o primeiro
  // terminar (debounce ja minimiza o caso, mas defesa extra).
  // Sessao 2026-05-14 (review Matias PR #345 #1): ref guarda a Promise do
  // save em voo (em vez de bool) pra que "Salvar e sair" e handleGerar
  // possam AGUARDAR o auto-save em voo antes de seguir — sem isso,
  // "Salvar e sair" virava no-op silencioso e handleGerar podia disparar
  // um segundo createMutation paralelo (contrato duplicado no DB).
  const persistingRef = React.useRef<Promise<void> | null>(null);
  // O último salvamento automático falhou? O diálogo de descarte e o cabeçalho dizem a
  // verdade em vez de "gravado até o último Continuar" (review do PR #772). Ref junto: o
  // sair lê depois de aguardar o save em voo, com o estado da closure já velho.
  const [autoSaveFalhou, setAutoSaveFalhou] = useState(false);
  const autoSaveFalhouRef = React.useRef(false);
  const marcarAutoSave = (falhou: boolean) => {
    autoSaveFalhouRef.current = falhou;
    setAutoSaveFalhou(falhou);
  };

  /**
   * Persistencia idempotente do rascunho. Reusada pelo "Salvar e sair"
   * (silent=false) e pelo auto-save em background (silent=true).
   * Em `silent=true`: nenhum toast, nenhum navigate, exceptions sao
   * logadas mas nao re-lancadas. Em `silent=false`: comportamento
   * antigo (toast + navigate pra listagem em sucesso).
   *
   * Mutex (review Matias #1):
   * - silent=true e save em voo: skipa — o debouncer agenda outro depois.
   * - silent=false e save em voo: AGUARDA o save em voo terminar e segue
   *   rodando o save manual (garante toast + navigate). NUNCA retorna mudo.
   */
  const persistirRascunho = async (silent: boolean): Promise<void> => {
    // Retomada por `?contratoId=` sem o contrato no ref (carregando, ou a carga falhou): salvar
    // criava OUTRO contrato com o formulário em branco.
    if (contratoIdFromQuery != null && !createdContratoRef.current) {
      if (!silent) {
        toast(
          retomandoEdicao
            ? { status: 'info', title: 'Aguarde o contrato carregar' }
            : {
                status: 'error',
                title: 'O contrato não carregou',
                description: 'Nada foi salvo. Recarregue a página para tentar de novo.',
              },
        );
      }
      return;
    }
    if (persistingRef.current) {
      if (silent) return;
      try {
        await persistingRef.current;
      } catch {
        // Erro do save anterior (provavelmente do auto-save) nao bloqueia
        // o "Salvar e sair" — seguimos e tentamos de novo aqui mesmo.
      }
    }

    const task = (async () => {
      const values = formMethods.getValues();
      const gravando = JSON.stringify(values);
      const payload = buildCreatePayload(values);
      // Le do ref pra ver o valor mais recente (state pode estar stale em
      // closures de auto-save que rodam em setTimeout/promise chains).
      let contrato = createdContratoRef.current;
      if (!contrato) {
        const createRes = await createMutation.mutateAsync(payload);
        contrato = createRes.data;
        // Atualiza ref ANTES do state — proxima invocacao do persistirRascunho
        // (mesmo antes do re-render) ja le o valor correto.
        createdContratoRef.current = contrato;
        setCreatedContrato(contrato);
      }
      // "Salvar e sair" só rebaixa pra 'rascunho' (e notifica o dept) quando o
      // contrato ainda é rascunho/draft. Já gerado: gerado->rascunho é transição
      // inválida (guard 409) — salvamos só os dados e o back rebaixa pra
      // 'em_revisao' se houve mudança (editou após gerar).
      const eraRascunhoEditavel = ehRascunhoEditavel(contrato.status);
      // Spread direto cobre todos os campos do payload (M3 da empresa,
      // socios estendidos com M1+M2+M2a). Sem lista manual paralela.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tipo_operacao: _tipoOp, contrato_pai_id: _paiId, ...patchData } = payload;
      await updateMutation.mutateAsync({
        id: contrato.id,
        data: {
          ...patchData,
          tipos_alteracao:
            values.tipo_operacao === 'alteracao' && (values.tipos_alteracao ?? []).length > 0
              ? (values.tipos_alteracao as any)
              : null,
          // Em auto-save NAO transicionamos pra rascunho (status='rascunho'
          // dispara notificacao cross-user — ruido). Em "Salvar e sair" SIM,
          // mas SO se ainda for rascunho/draft (gerado->rascunho = 409 no guard;
          // contrato gerado salva so dados e o back rebaixa pra 'em_revisao').
          status: silent || !eraRascunhoEditavel ? undefined : ('rascunho' as any),
        },
      });
      formGravadoRef.current = gravando;
      if (!silent) {
        toast({
          status: 'success',
          title: eraRascunhoEditavel ? 'Rascunho salvo' : 'Alterações salvas',
          description: eraRascunhoEditavel
            ? 'O departamento societário foi notificado e pode continuar a edição.'
            : 'O contrato voltou para "Em revisão" — gere novamente para atualizar o documento.',
        });
        navigate('/societario/contratos');
      }
    })();

    persistingRef.current = task;
    setSalvando(true);
    try {
      await task;
      marcarAutoSave(false);
      setSalvoEm(new Date());
    } catch (err: any) {
      if (!silent) {
        toast({
          status: 'error',
          title: 'Falha ao salvar rascunho',
          description: extractApiErrorMessage(err),
        });
      } else {
        // Sem toast a cada Continuar (422 em campo meio preenchido é normal): o cabeçalho
        // mostra a falha e o diálogo de descarte não afirma que gravou.
        marcarAutoSave(true);
        // eslint-disable-next-line no-console
        console.warn('auto-save falhou', err);
      }
    } finally {
      // So limpa o ref se ainda for esta task (evita race com outro save
      // que tomou o slot enquanto aguardavamos).
      if (persistingRef.current === task) persistingRef.current = null;
      setSalvando(false);
    }
  };

  const handleSalvarESair = async () => {
    setIsSavingDraft(true);
    try {
      await persistirRascunho(false);
      // Salvo de propósito pelo operador — não descartar ao sair.
      committedRef.current = true;
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Descartar-ao-sair-sem-salvar: se há um contrato CRIADO nesta sessão (ex:
  // alteração cujo upload de vigente já criou o registro no banco) e o operador
  // sai SEM "Salvar e sair" nem gerar, confirma e DELETA o rascunho — evita os
  // contratos-lixo na listagem. NUNCA descarta contrato carregado da listagem
  // (`wasLoadedRef`) nem já commitado (`committedRef`/`geracaoOk`). Lê o id pelo
  // ref (`createdContratoRef`) pra pegar o valor mais recente em closure async.
  const handleSairDoWizard = async () => {
    // Aguarda um save em voo (se houver) — evita ler estado stale ou deletar um
    // contrato sendo salvo. Pós-await, committedRef pode já ter virado true.
    if (persistingRef.current) {
      try {
        await persistingRef.current;
      } catch {
        // erro do save já foi tratado/toastado dentro do persistirRascunho.
      }
    }
    const id = createdContratoRef.current?.id;
    const descartavel = id != null && !wasLoadedRef.current && !committedRef.current && !geracaoOk;
    if (descartavel && id != null) {
      const confirmou = await new Promise<boolean>(resolve => {
        showDialog({
          status: 'warning',
          title: 'Descartar rascunho?',
          description: textoDoDescarte(autoSaveFalhouRef.current),
          confirmLabel: 'Descartar e sair',
          cancelLabel: 'Continuar editando',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!confirmou) return;
      try {
        await deleteContrato(id);
        // O card do processo apontava para este rascunho: desfaz o vínculo — só se ainda
        // aponta (em outra aba o card pode ter trocado de contrato). Se falhar, o card
        // trata o contrato que sumiu como "sem contrato".
        if (legalizacaoIdFromQuery != null) {
          try {
            const { data: processo } = await obterLegalizacao(legalizacaoIdFromQuery);
            if (processo?.contrato_societario_id === id)
              await atualizarLegalizacao(legalizacaoIdFromQuery, { contrato_societario_id: null });
          } catch {
            // O card mostra o aviso de contrato apagado.
          }
          legVinculadoRef.current = null;
        }
      } catch (err) {
        // Falha ao descartar não deve prender o operador na página.
        // eslint-disable-next-line no-console
        console.warn('Falha ao descartar rascunho ao sair', err);
      }
    } else if (
      id != null &&
      !geracaoOk &&
      !salvaAoContinuar(createdContratoRef.current?.status) &&
      formAlterado &&
      JSON.stringify(formMethods.getValues()) !== formGravadoRef.current
    ) {
      // Contrato que não salva no Continuar (gerado): sair com o formulário alterado perdia
      // as edições calado (review do PR #772).
      const sair = await new Promise<boolean>(resolve => {
        showDialog({
          status: 'warning',
          title: 'Sair sem salvar?',
          description:
            'As edições deste contrato não foram gravadas. Use "Salvar e sair" para gravá-las.',
          confirmLabel: 'Sair sem salvar',
          cancelLabel: 'Continuar editando',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!sair) return;
    }
    navigate('/societario/contratos');
  };

  // Sessao 2026-05-15: AUTO-SAVE REMOVIDO. Estava disparando PATCH com
  // `socios: payload.socios` (REPLACE-ALL em theory) mas o backend gerava
  // duplicacao a cada salvamento — operadores reportaram socios duplicados
  // em todos os contratos editados em prod. Mantemos apenas o save MANUAL
  // via "Salvar e sair" (handleSalvarESair). Operador agora controla
  // explicitamente quando persistir.
  //
  // Investigar separadamente: por que o PATCH com socios duplica em vez
  // de substituir? Comentario no schema (`useSocietario.ts`) afirma REPLACE-ALL
  // mas o comportamento observado em prod e APPEND. Provavel bug no
  // controller / sync layer do backend.

  // Pos-Fase 6: ao sair do step contrato_vigente pela primeira vez, dispara PATCH
  // advance_from_initial=true. Backend transiciona o status pra `rascunho` e libera os
  // validators. Usado pelo Continuar e pelo salto das bolinhas.
  const avancarDoInicial = async (): Promise<boolean> => {
    if (advancedFromInitial || !createdContrato?.id) return true;
    try {
      await updateMutation.mutateAsync({
        id: createdContrato.id,
        data: { advance_from_initial: true },
      });
      setAdvancedFromInitial(true);
      return true;
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'Falha ao avançar do contrato vigente',
        description: extractApiErrorMessage(err),
      });
      return false;
    }
  };

  const salvarAoAvancar = () => {
    // Grava ao passar de passo (25/09): o que foi preenchido nao se perde se a aba fechar, e a
    // retomada abre com os dados. So' com o contrato ja criado (na alteracao, o upload do vigente
    // cria) e sem esperar; o Gerar aguarda um save em voo. O PATCH troca os socios na mesma
    // transacao (soft-delete + insert), sem a duplicacao que tirou o auto-save em 15/05.
    // Nao no gerado: com dados no PATCH o back o rebaixa para em_revisao, que so' vai para
    // cancelado; navegar num contrato gerado aberto no wizard o estragava calado. O em_revisao
    // salva: o back nao mexe no status dele (salvaAoContinuar).
    const contratoAberto = createdContratoRef.current;
    if (contratoAberto && salvaAoContinuar(contratoAberto.status)) {
      persistirRascunho(true).catch(() => undefined);
    }
  };

  const handleNext = async () => {
    const ok = await validateStep(currentStep);
    if (!ok) return;
    if (currentStep === 'contrato_vigente' && !(await avancarDoInicial())) return;
    salvarAoAvancar();
    const nextIdx = Math.min(stepsList.length - 1, currentIdx + 1);
    setCurrentStep(stepsList[nextIdx].key as StepKey);
  };

  // Bolinhas (28/09): para trás, livre (como sempre foi); para a frente, só até a etapa
  // mais adiantada já alcançada, validando CADA etapa do caminho como o Continuar. Para
  // na primeira com erro, que fica na tela com os campos marcados — pular não fura a
  // validação.
  const irParaEtapa = async (destino: StepKey) => {
    const destinoIdx = stepsList.findIndex(e => e.key === destino);
    if (destinoIdx < 0 || destinoIdx === currentIdx) return;
    if (destinoIdx < currentIdx) {
      setCurrentStep(destino);
      return;
    }
    if (destinoIdx > alcancadoIdx) return;
    for (let i = currentIdx; i < destinoIdx; i += 1) {
      const etapa = stepsList[i].key as StepKey;
      // Em sequência de propósito: para na primeira etapa com erro.
      // eslint-disable-next-line no-await-in-loop
      const ok = await validateStep(etapa);
      // eslint-disable-next-line no-await-in-loop
      if (!ok || (etapa === 'contrato_vigente' && !(await avancarDoInicial()))) {
        // Parou adiante de onde estava: grava, como o Continuar de cada etapa que passou.
        if (i > currentIdx) salvarAoAvancar();
        setCurrentStep(etapa);
        return;
      }
    }
    salvarAoAvancar();
    setCurrentStep(destino);
  };

  // Trocar de etapa leva ao topo dela (28/09: a etapa nova abria na altura em que o
  // operador estava na anterior). Só rola se as bolinhas estão escondidas (acima da tela
  // ou atrás do cabeçalho azul do admin, que é fixo): quem já está no topo não vê a
  // página pular.
  const topoDasEtapasRef = useRef<HTMLDivElement | null>(null);
  const colunaRef = useRef<HTMLDivElement | null>(null);
  const primeiraEtapaRef = useRef(true);
  useEffect(() => {
    if (primeiraEtapaRef.current) {
      primeiraEtapaRef.current = false;
      return;
    }
    const topo = topoDasEtapasRef.current;
    if (topo && topo.getBoundingClientRect().top < TOPO_ABAIXO_DO_CABECALHO) {
      topo.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentStep]);

  const handleBack = () => {
    const prevIdx = Math.max(0, currentIdx - 1);
    setCurrentStep(stepsList[prevIdx].key as StepKey);
  };

  // Se a lista mudou (ex: usuario desmarcou quadro_societario enquanto estava
  // no step Socios) e o currentStep nao existe mais, voltamos para o step
  // anterior valido na nova lista.
  React.useEffect(() => {
    if (stepsList.findIndex(s => s.key === currentStep) === -1) {
      // Pega o step anterior mais proximo que ainda exista, ou o primeiro.
      setCurrentStep(stepsList[stepsList.length - 1]?.key as StepKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsList.map(s => s.key).join('|')]);

  const createMutation = useMutation({
    mutationFn: createContrato,
  });

  const gerarMutation = useMutation({
    mutationFn: gerarContrato,
  });

  // Preview real da alteração (parte C): deriva o MESMO corpo que o handleGerar
  // manda pro /gerar (patches + vigente_hash + quadro_vigente_override), mas SEM
  // os toasts de roteamento — se faltar dado, retorna undefined e o back devolve
  // "Preview indisponível" gracioso. Read-only, advisory. MANTER EM SINCRONIA
  // com o bloco de derivação do handleGerar (patches/override) abaixo.
  const montarBodyPreviewAlteracao = async (): Promise<Record<string, unknown> | undefined> => {
    const values = formMethods.getValues();
    if (values.tipo_operacao !== 'alteracao' || createdContrato?.id == null) {
      return undefined; // constituição/transformação: back renderiza do contrato
    }
    let preview;
    try {
      preview = await parsearVigentePreview(createdContrato.id);
    } catch {
      return undefined; // sem vigente parseável → back mostra "faça upload do vigente"
    }
    const tipos = (values.tipos_alteracao ?? []) as any[];
    const { patches } = derivarPatchesV3(
      tipos,
      {
        razao_social: values.razao_social ?? null,
        nome_fantasia: values.nome_fantasia ?? null,
        capital_social: values.capital_social ?? null,
        capital_qtd_quotas: values.capital_qtd_quotas ?? null,
        capital_valor_quota: values.capital_valor_quota ?? null,
        endereco: values.endereco ?? null,
        endereco_estruturado: values.endereco_estruturado ?? null,
        filiais: values.filiais ?? null,
        filiais_fechar: values.filiais_fechar ?? null,
        filiais_alterar: values.filiais_alterar ?? null,
        objeto_social: values.objeto_social ?? null,
        cnaes: (values.cnaes ?? []) as any[],
        atuais: atuaisDoVigente(createdContrato?.contrato_vigente_dados_extraidos),
      },
      preview.parser_json ?? {},
    );
    // Guard (review #523): espelha a cobertura do handleGerar — sem patch E sem
    // mudança de quadro/admin, a geração v3 recusaria ("Nenhuma alteração para
    // aplicar"). Retorna undefined pra não previsualizar um consolidado de um
    // caso NÃO-gerável (o back mostra "indisponível").
    const alteraQuadroPreview = tipos.some(t =>
      (TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS as readonly string[]).includes(t),
    );
    if (patches.length === 0 && !alteraQuadroPreview) {
      return undefined;
    }
    const body: Record<string, unknown> = {
      tipos_alteracao: tipos.length > 0 ? tipos : null,
      consolidar: true,
      patches: patches.length > 0 ? patches : null,
      vigente_hash: preview.vigente_hash ?? null,
    };
    const tiposAlt = tipos as string[];
    // 2026-08-06: `capital` TAMBEM muda o quadro. O wizard abre o passo Socios
    // quando capital e marcado e o pre-flight EXIGE que as quotas somem o capital
    // novo — ou seja, o operador e obrigado a edita-las. Mandar `values.socios`
    // (o POS) como se fosse o vigente fazia o motor concluir que a QUANTIDADE de
    // quotas nao mudou e que subiu o VALOR da quota, e ai ele distribuia o aumento
    // proporcionalmente entre TODOS. Resultado: socio que nao pos um centavo
    // aparecia integralizando. Fecha aritmeticamente, nenhum validador barra, e o
    // documento sai errado calado. (So aparece quando a proporcao muda; com todos
    // subindo na mesma proporcao a distorcao se cancela — por isso passou batido.)
    // 2026-08-07 (review do PR #626): usa a CONSTANTE canonica em vez de listar
    // tipos a mao. `administracao` tinha ficado de fora e e' alcancavel: com so ele
    // marcado o passo Socios abre (computeSteps consome a mesma constante), o campo
    // de quotas fica livre (ninguem saindo -> nao e' destino de cessao) e o aspecto
    // 'quotas' e' oferecido em acao='alterar'. Bastava mexer numa quota pra cair na
    // MESMA classe de erro do capital: mandar o POS rotulado como vigente, fechando
    // aritmeticamente e sem validador nenhum barrando.
    if (!tiposAlt.some(t => (TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS as readonly string[]).includes(t))) {
      body.quadro_vigente_override = (values.socios ?? [])
        .filter(s => s.acao !== 'adicionar')
        .map(s => ({
          nome: (s.nome ?? '').trim(),
          cpf: s.cpf ?? null,
          cnpj: s.cnpj ?? null,
          quotas: Number(s.quotas ?? 0) || 0,
          tipo_vinculo: s.tipo_vinculo ?? null,
        }));
    } else {
      const vig: any = createdContrato?.contrato_vigente_dados_extraidos ?? {};
      const quadroVig: any[] = Array.isArray(vig.quadro_socios_atual)
        ? vig.quadro_socios_atual
        : [];
      if (quadroVig.length > 0) {
        body.quadro_vigente_override = quadroVig.map((s: any) => ({
          nome: String(s?.nome ?? '').trim(),
          cpf: s?.cpf ?? null,
          cnpj: s?.cnpj ?? null,
          quotas: Number(s?.quotas ?? 0) || 0,
          tipo_vinculo: s?.tipo_vinculo ?? null,
        }));
      }
    }
    return body;
  };

  // PATCH do Gerar: cria o contrato (constituição sem rascunho) ou grava o que o operador
  // editou desde o pré-preenchimento. Devolve o contrato a gerar.
  const gravarAntesDeGerar = async (values: IWizardFormValues): Promise<IContratoSocietario> => {
    // Idempotência: se já criamos o contrato numa tentativa anterior (ex: erro
    // na geração após o create), reaproveitamos em vez de criar duplicado.
    // Le do ref pra cobrir race com auto-save em flight (state nao
    // refletiu o create disparado pelo auto-save ainda).
    const existente = createdContratoRef.current;
    if (!existente) {
      // Retomada cuja carga falhou: gerar criaria outro contrato (como o "Salvar e sair").
      if (contratoIdFromQuery != null) {
        throw new Error('O contrato não carregou. Recarregue a página para tentar de novo.');
      }
      const createRes = await createMutation.mutateAsync(buildCreatePayload(values));
      createdContratoRef.current = createRes.data;
      setCreatedContrato(createRes.data);
      return createRes.data;
    }
    // Pos-Fase 6: contrato pre-existente vem do upload do contrato vigente
    // (status era rascunho_inicial; ja transicionou pra rascunho via
    // advance_from_initial no handleNext). Fazemos PATCH com TODOS os
    // campos do form pra persistir tudo que o usuario editou desde o
    // pre-fill — sem isso, gerar enviaria campos crus (apenas o que veio
    // do extractor) e ignoraria edicoes do usuario.
    const payload = buildCreatePayload(values);
    // Lista de campos antes era enumerada manualmente — gap silencioso
    // identico ao bug do `buildCreatePayload` (lista paralela ao schema).
    // Spread direto cobre tudo que buildCreatePayload monta (M3 da
    // empresa, socios estendidos, etc) sem precisar manter lista
    // espelhada. `tipo_operacao` e `contrato_pai_id` saem do PATCH
    // (Update schema nao aceita). `tipos_alteracao` e adicionado
    // condicional (esse vem do form, nao do buildCreatePayload).
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, camelcase
    const { tipo_operacao: _to, contrato_pai_id: _cp, ...patchData } = payload;
    const gravando = JSON.stringify(values);
    await updateMutation.mutateAsync({
      id: existente.id,
      data: {
        ...patchData,
        // Sessao 2026-05-08 (contrato 73): persiste tipos_alteracao no
        // PATCH pra que retomada do rascunho mantenha a selecao.
        tipos_alteracao:
          values.tipo_operacao === 'alteracao' && (values.tipos_alteracao ?? []).length > 0
            ? (values.tipos_alteracao as any)
            : null,
      },
    });
    formGravadoRef.current = gravando;
    marcarAutoSave(false);
    // O PATCH acima gravou tudo o que o operador preencheu: a partir daqui
    // o contrato e' trabalho salvo. Sem isto, na alteracao (geracao
    // assincrona, `geracaoOk` so' no fim) uma falha deixava o sair do
    // wizard oferecer "Voce nao salvou... Descartar" — e apagava o contrato.
    committedRef.current = true;
    return existente;
  };

  // A tela de sucesso mostra a situação e o nome do contrato; o objeto local ainda é o
  // do rascunho (a geração mudou o status no back). Relê, como faz o caminho assíncrono
  // (handleLoadingTerminal); sem rede, ao menos o status acompanha.
  const releContratoGerado = async (id: number, status: IContratoSocietario['status']) => {
    try {
      const res = await getContrato({ queryKey: ['contrato', id] } as any);
      setCreatedContrato(res.data);
    } catch {
      setCreatedContrato(atual => (atual ? { ...atual, status } : atual));
    }
  };

  const gerarDoWizard = async () => {
    // ⚠️ Regerar DESCARTA a versão revisada: ela foi ajustada em cima do DOCX
    // anterior, e manter a referência apontando pra um ato que não existe mais
    // é a classe de bug do render stale — nada quebra, e ninguém percebe.
    // O backend limpa a coluna de qualquer forma (rede de segurança); aqui é
    // onde o operador tem chance de desistir.
    if (createdContratoRef.current?.contrato_editado_s3_key) {
      const seguir = await new Promise<boolean>(resolve => {
        showDialog({
          status: 'warning',
          title: 'Este contrato tem uma versão revisada',
          description:
            'Você enviou um DOCX ajustado à mão para este ato. Regerar vai ' +
            'descartá-lo e o documento voltará a ser o gerado pelo sistema — ' +
            'os seus ajustes precisarão ser refeitos. Deseja continuar?',
          confirmLabel: 'Regerar e descartar',
          cancelLabel: 'Manter a versão revisada',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        });
      });
      if (!seguir) return;
    }
    // Valida o passo Sócios quando ele faz parte do fluxo (constituição/
    // transformação sempre; alteração só quando mexe em sócios). Sem isto um
    // sócio novo PJ com representante incompleto escapava pro 422
    // SOCIO_ADMITIDO_INCOMPLETO do backend (toast confuso) em vez de erro
    // inline no passo. handleGerar revalidava só empresa + revisao.
    if (stepsList.some(s => s.key === 'socios')) {
      const okSocios = await validateStep('socios');
      if (!okSocios) return;
    }
    // Chat da administração obrigatório (29/09): sem redação aprovada, volta para a etapa.
    if (stepsList.some(s => s.key === 'administracao') && !(await validateStep('administracao'))) {
      setCurrentStep('administracao');
      return;
    }
    if (stepsList.some(s => s.key === 'clausulas') && !(await validateStep('clausulas'))) {
      setCurrentStep('clausulas');
      return;
    }
    const okEmpresa = await validateStep('empresa');
    if (!okEmpresa) return;
    const okRevisao = await validateStep('revisao');
    if (!okRevisao) return;
    // `exigeAdmin` calculado no nivel do componente. Mesma logica:
    // - constituicao: sempre (toda LTDA precisa de >=1 administrador definido).
    // - alteracao com `quadro_societario` ou `administracao` marcado: o
    //   operador esta alterando socios/admin, precisa marcar quem administra.
    // - alteracao apenas de capital/endereco/razao/objeto: socios herdam do
    //   contrato vigente, nao precisa revalidar admin aqui.
    // Le no ato (getValues), sem subscricao — este e um handler de evento, nao
    // precisa reatividade. Era o outro consumo do `useWatch` da raiz.
    if (exigeAdmin && !temAdministradorEm(formMethods.getValues('socios'))) {
      toast({
        status: 'error',
        title: 'Nenhum administrador cadastrado',
        description: 'Volte ao passo Sócios e marque ao menos 1 sócio como administrador.',
      });
      return;
    }

    // Pre-flight da redistribuicao de quotas (acao=sair). Backend bloqueia via
    // V_REDISTRIBUICAO_QUOTAS_BATE_VIGENTE so apos consumir IA — barramos antes
    // pra economizar custo + evitar UX confusa de "gerou mas tem que regerar".
    const valuesPre = formMethods.getValues();

    // Constituição (item 6): a soma das quotas dos sócios — excluindo o
    // administrador NÃO-sócio, que não detém quotas — tem que bater com a
    // quantidade total de quotas definida no passo Empresa. O backend valida;
    // barramos antes pra UX clara (sócios distribuem a qtd total do capital).
    if (valuesPre.tipo_operacao === 'constituicao') {
      const qtdTotal = Number(valuesPre.capital_qtd_quotas);
      if (Number.isFinite(qtdTotal) && qtdTotal > 0) {
        // Fase B (2026-06-25): a unipessoal já aceita valor de quota variável
        // (o back valida capital == qtd × valor) — o bloqueio "uni exige R$ 1,00"
        // saiu. Resta garantir que a soma das quotas dos sócios bate com a qtd.
        const somaQuotas = (valuesPre.socios ?? [])
          .filter(s => s.tipo_vinculo !== 'administrador')
          .reduce((acc, s) => acc + (Number(s.quotas) || 0), 0);
        if (somaQuotas !== qtdTotal) {
          toast({
            status: 'error',
            title: 'Quotas não batem com o capital',
            description:
              `O capital tem ${qtdTotal} quotas, mas os sócios somam ${somaQuotas}. ` +
              `Volte ao passo Sócios e ajuste as quotas para somar ${qtdTotal}.`,
          });
          return;
        }
      }
    }

    const vigente: any = createdContrato?.contrato_vigente_dados_extraidos ?? {};
    const quadroPre: any[] = Array.isArray(vigente.quadro_socios_atual)
      ? vigente.quadro_socios_atual
      : [];
    const vigentePorDocPre: Record<string, number | null | undefined> = {};
    quadroPre.forEach(s => {
      const doc = String(s?.cpf || s?.cnpj || '').replace(/\D/g, '');
      if (doc) vigentePorDocPre[doc] = s?.quotas;
    });
    const erroRedistribuicao = (valuesPre.socios ?? []).reduce<string | null>((acc, s, idx) => {
      if (acc) return acc;
      if (s.acao !== 'sair') return null;
      const doc = String(s.cpf || s.cnpj || '').replace(/\D/g, '');
      const quotasVig = doc ? vigentePorDocPre[doc] : null;
      if (quotasVig == null) return null; // sem vigente, nao podemos comparar; backend valida
      const soma = (s.redistribuicao_quotas ?? []).reduce((a, d) => a + (Number(d?.qtd) || 0), 0);
      if (soma !== quotasVig) {
        return `Sócio ${idx + 1} (${s.nome || 'sem nome'}): redistribuição soma ${soma}, deveria somar ${quotasVig} (quotas vigentes).`;
      }
      return null;
    }, null);
    if (erroRedistribuicao) {
      toast({
        status: 'error',
        title: 'Redistribuição de quotas inválida',
        description: erroRedistribuicao,
      });
      return;
    }

    // GAP 1 (2026-06-10): se o capital muda, a soma das quotas dos sócios
    // pós-alteração (manter/alterar/adicionar) tem que bater com o total de
    // quotas do novo capital — senão a tabela consolidada fica incoerente com
    // a prosa do capital. O backend barra com V_CAPITAL_QUOTAS_BATE_TOTAL
    // (422); barramos antes pra UX clara. Não dispara quando só o valor da
    // quota muda (capital_qtd_quotas igual) nem quando o form não tem sócios.
    const tiposAltCap = (valuesPre.tipos_alteracao ?? []) as string[];
    const capitalQtd = Number(valuesPre.capital_qtd_quotas);
    if (
      tiposAltCap.includes('capital') &&
      Number.isFinite(capitalQtd) &&
      capitalQtd > 0 &&
      (valuesPre.socios ?? []).length > 0
    ) {
      const somaQuotasPos = (valuesPre.socios ?? []).reduce((acc, s) => {
        if (s.acao === 'sair') return acc;
        return acc + (Number(s.quotas) || 0);
      }, 0);
      if (somaQuotasPos !== capitalQtd) {
        toast({
          status: 'error',
          title: 'Quotas não batem com o capital',
          description:
            `O novo capital tem ${capitalQtd} quotas, mas os sócios somam ${somaQuotasPos}. ` +
            `Distribua as quotas dos sócios no passo "Sócios" para somar ${capitalQtd} ` +
            `(ou ajuste o total de quotas do capital).`,
        });
        return;
      }
    }

    // Cessão capital-constante (2026-06-11): mudança de quadro SEM alterar
    // capital (ex: cessão entre sócios, admissão por cessão) — a soma das
    // quotas pós (manter/alterar/adicionar, excluindo sair) tem que continuar
    // batendo com o total vigente, senão a cessão fica desbalanceada. O
    // backend barra (V_QUOTAS_TOTAL_BATE_VIGENTE_V3, 422); barramos antes pra
    // UX clara e economizar processamento.
    if (
      tiposAltCap.includes('quadro_societario') &&
      !tiposAltCap.includes('capital') &&
      (valuesPre.socios ?? []).length > 0
    ) {
      const totalVigentePre = quadroPre.reduce((a, s) => a + (Number(s?.quotas) || 0), 0);
      const somaQuotasPosQuadro = (valuesPre.socios ?? []).reduce((acc, s) => {
        if (s.acao === 'sair') return acc;
        return acc + (Number(s.quotas) || 0);
      }, 0);
      if (totalVigentePre > 0 && somaQuotasPosQuadro !== totalVigentePre) {
        toast({
          status: 'error',
          title: 'Quotas não batem com o quadro vigente',
          description:
            `Sem alteração de capital, a soma das quotas dos sócios deve permanecer ` +
            `${totalVigentePre} (total vigente), mas somam ${somaQuotasPosQuadro}. ` +
            `Ajuste a cessão/redistribuição de quotas entre os sócios.`,
        });
        return;
      }
    }

    // Sessao 2026-05-14 (review Matias PR #345 #1): aguarda o auto-save em
    // voo (se houver) terminar ANTES de checar createdContratoRef. Sem isso,
    // o auto-save pode estar no meio do createMutation e o handleGerar
    // dispara um SEGUNDO createMutation em paralelo — o backend persiste 2
    // contratos. O ref `createdContratoRef` so e atualizado apos o create
    // do auto-save resolver; esperar ele garante leitura coerente.
    if (persistingRef.current) {
      try {
        await persistingRef.current;
      } catch {
        // Erro do auto-save nao bloqueia geracao — seguimos com o estado
        // que conseguimos persistir; createdContratoRef pode ja estar setado.
      }
    }

    try {
      const values = formMethods.getValues();
      // O PATCH do Gerar ocupa o mesmo slot do auto-save (review do PR #772): "Salvar e
      // sair" no meio aguarda este PATCH em vez de sobrepor outro. O slot nunca rejeita
      // (quem aguarda não precisa do erro, que segue para o catch daqui).
      const gravacao = gravarAntesDeGerar(values);
      const slot = gravacao.then(
        () => undefined,
        () => undefined,
      );
      persistingRef.current = slot;
      const contrato = await gravacao.finally(() => {
        if (persistingRef.current === slot) persistingRef.current = null;
      });

      // Sessao 2026-05-11 (item 25 Fase 3): valida no backend antes de
      // chamar /gerar (que custa IA). Críticos não-overridable bloqueiam
      // a geracao. Críticos overridable abrem dialog "Há N avisos. Gerar
      // mesmo assim?" — operador senior decide. Falha de chamada da
      // /validar nao bloqueia: backend roda gate pre-IA no /gerar
      // (defesa em camadas).
      //
      // 2026-05-26: validators camada 3 (`V_SOCIO_PF_CAMPOS`,
      // `V_FORO_PRESENTE`, `V_FORO_COERENTE_COM_SEDE`) agora sao
      // engine-agnosticos — aceitam coluna legacy OU estruturado v3.
      // Apaga o skip anterior pra constituicao (sem mais falsos positivos
      // "endereco vazio" / "foro nao informado").
      // ⚠️ O acompanhamento abre AQUI, no começo do trecho pesado: a validação
      // e o parse do vigente vêm a seguir, e é nesse intervalo que o operador
      // ficava olhando a tela parada. Duas tentativas anteriores erraram o
      // ponto — abrir na resposta do /gerar, e depois só antes do parse — e nos
      // dois casos o /validar (que baixava o docx do S3) já tinha consumido o
      // tempo todo antes de aparecer qualquer sinal.
      //
      // Os diálogos de confirmação que podem vir a seguir fecham o modal antes
      // de perguntar, e reabrem depois — senão ficariam escondidos atrás dele.
      setPreparandoGeracao(true);

      try {
        const issues = await validarContrato(contrato.id);
        const criticos = issues.filter(i => i.severidade === 'critico');
        const bloqueantes = criticos.filter(i => !i.overridable);
        const overridables = criticos.filter(i => i.overridable);
        if (bloqueantes.length > 0) {
          const msgs = bloqueantes
            .slice(0, 3)
            .map(b => `• ${b.mensagem}`)
            .join('\n');
          toast({
            status: 'error',
            title: `${bloqueantes.length} problema(s) crítico(s) precisam ser corrigidos`,
            description:
              msgs + (bloqueantes.length > 3 ? `\n…e mais ${bloqueantes.length - 3}` : ''),
            duration: 9000,
          });
          return;
        }
        if (overridables.length > 0) {
          const msgs = overridables.map(o => `• ${o.mensagem}`).join('\n');
          // O acompanhamento sai da frente pra perguntar, e volta se o
          // operador confirmar — modal por cima de dialogo esconde a pergunta.
          setPreparandoGeracao(false);
          const confirmou = await new Promise<boolean>(resolve => {
            showDialog({
              status: 'warning',
              title: `${overridables.length} aviso(s) detectado(s)`,
              description: `${msgs}\n\nDeseja gerar o contrato mesmo assim?`,
              confirmLabel: 'Gerar mesmo assim',
              cancelLabel: 'Voltar e corrigir',
              onConfirm: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!confirmou) return;
          setPreparandoGeracao(true);
        }
      } catch (errValid) {
        // eslint-disable-next-line no-console
        console.warn('Validação pré-gerar falhou — backend roda gate próprio', errValid);
      }

      // Sessao 2026-05-14 (review Matias PR #345 #8): avisos criticos da
      // EXTRACAO IA do contrato vigente — backend nao valida porque a fonte
      // de verdade e o PDF do operador (IA pode ler errado: CNPJ DV invalido,
      // capital ≤ 0, razao vazia, CPF/CNPJ de socio invalido). Antes
      // `avaliarAtencoes` so renderizava um Alert amarelo no preview do step
      // e operador seguia queimando IA com vigente sabidamente errado.
      // So bloqueia em alteracao com vigente anexado (constituicao nao tem).
      if (values.tipo_operacao === 'alteracao' && contrato.contrato_vigente_dados_extraidos) {
        const criticosVigente = avaliarAtencoesCriticos(contrato.contrato_vigente_dados_extraidos);
        if (criticosVigente.length > 0) {
          const msgsVig = criticosVigente
            .slice(0, 5)
            .map(c => `• ${c}`)
            .join('\n');
          const sufixoVig =
            criticosVigente.length > 5 ? `\n…e mais ${criticosVigente.length - 5}` : '';
          setPreparandoGeracao(false);
          const confirmouVigente = await new Promise<boolean>(resolve => {
            showDialog({
              status: 'warning',
              title: `${criticosVigente.length} aviso(s) crítico(s) na extração do contrato vigente`,
              description: `${msgsVig}${sufixoVig}\n\nA IA usa esses dados extraídos como base do "estado atual" do contrato — se estiverem errados, o contrato gerado pode sair com inconsistências. Gerar mesmo assim?`,
              confirmLabel: 'Gerar mesmo assim',
              cancelLabel: 'Voltar e revisar',
              onConfirm: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!confirmouVigente) return;
          setPreparandoGeracao(true);
        }
      }

      // Roteamento de engine:
      // - constituicao → v3 (docxtpl determinístico PF/PJ uni/pluri). Backend
      //   `decidir_engine` refina pelo `len(socios)`.
      // - alteracao → v3 quando todos `tipos_alteracao` marcados forem
      //   cobertos em D+E (`razao_social`, `endereco`, `capital`,
      //   `objeto_social`, `quadro_societario`). Senão cai pra v2 (legacy
      //   IA-first) — `administracao`, `outras` continuam em v2 até a Onda F.
      //
      // v3 alteração exige `patches[]` derivados do vigente parseado +
      // `vigente_hash` (lock G-5 — strict ativo A.39). Buscamos via
      // `GET /parsear-vigente-preview` ao montar a payload.
      let engineEscolhido: 'v3';
      let patchesV3: import('../../../../hooks/useSocietario').IPatchClausula[] | null = null;
      let vigenteHashV3: string | null = null;

      if (ehFluxoConstituinte(values.tipo_operacao)) {
        // Constituição e Transformação geram direto em v3 (sem vigente a
        // parsear — o EI não tem contrato social; backend monta a Parte B do zero).
        engineEscolhido = 'v3';
      } else {
        // Alteração — tenta rotear v3 D+E.
        try {
          const preview = await parsearVigentePreview(contrato.id);
          const tipos = (values.tipos_alteracao ?? []) as any[];
          const { patches, cobertoEmDE, motivos, avisos } = derivarPatchesV3(
            tipos,
            {
              razao_social: values.razao_social ?? null,
              nome_fantasia: values.nome_fantasia ?? null,
              capital_social: values.capital_social ?? null,
              capital_qtd_quotas: values.capital_qtd_quotas ?? null,
              capital_valor_quota: values.capital_valor_quota ?? null,
              endereco: values.endereco ?? null,
              endereco_estruturado: values.endereco_estruturado ?? null,
              filiais: values.filiais ?? null,
              filiais_fechar: values.filiais_fechar ?? null,
              filiais_alterar: values.filiais_alterar ?? null,
              objeto_social: values.objeto_social ?? null,
              cnaes: (values.cnaes ?? []) as any[],
              atuais: atuaisDoVigente(createdContrato?.contrato_vigente_dados_extraidos),
            },
            preview.parser_json ?? {},
          );
          // Gera, mas pede conferência no documento (filial sem bairro/CEP, objeto com o
          // texto atual): aviso que não bloqueia.
          if (cobertoEmDE && avisos.length > 0) {
            toast({
              status: 'warning',
              title: 'Confira no documento gerado',
              description: avisos.join(' '),
              duration: 12000,
              isClosable: true,
            });
          }
          // Bloco 13 (teste manual 2026-06-10): alteração de quadro PURA não
          // gera patch (o diff de sócios é derivado no backend), mas o v3
          // consolida o quadro sem patches. Antes só roteava pro v3 com
          // patches>0 → admissão/retirada/cessão pura caía no v2 (IA).
          // Wave 2 (2026-06-19): roteia pro v3 quando o tipo afeta sócios/admins
          // (quadro_societario OU administracao). Ambos não geram patch explícito
          // (derivados do socios[] no backend) — sem isto, administração cairia no
          // v2 por patches.length===0.
          const alteraQuadro = tipos.some(t =>
            (TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS as readonly string[]).includes(t),
          );
          // Outras cláusulas (15/10): o texto aprovado no chat é a mudança, sem patch.
          if (cobertoEmDE && (patches.length > 0 || alteraQuadro || tipos.includes('clausulas'))) {
            engineEscolhido = 'v3';
            patchesV3 = patches;
            vigenteHashV3 = preview.vigente_hash ?? null;
          } else {
            // v3-only (2026-06-23): não gera em v2. Sem patch nem mudança de
            // quadro = nada pra aplicar (valor igual ao vigente / campo vazio);
            // 'outras' (oculto) também cai aqui via contrato salvo antigo.
            toast({
              status: 'error',
              title: 'Nenhuma alteração para aplicar',
              description: `Os tipos marcados não geraram nenhuma mudança detectável para a geração v3${
                motivos.length ? `: ${motivos.join(' ')}` : ''
              }. Confirme que você alterou algum valor (razão social, endereço, capital, objeto social) ou ajustou o quadro de sócios.`,
              duration: 14000,
            });
            // aborta antes de gerar: o modal aberto la em cima sai junto,
            // senao fica por cima da tela sem nada acontecendo.
            setPreparandoGeracao(false);
            return;
          }
        } catch (errPreview: any) {
          // Alteração é v3-only (2026-06-23): QUALQUER erro do preview bloqueia
          // a geração — nunca cai em v2. Diferencia só a MENSAGEM por causa:
          // vigente ilegível (VIGENTE_ILEGIVEL), falha de sistema (5xx/network),
          // sem acesso (403) ou sem vigente/outro (404 etc).
          const statusPreview = extractApiErrorStatus(errPreview);
          const codigoPreview = extractApiErrorCode(errPreview);
          if (codigoPreview === 'VIGENTE_ILEGIVEL') {
            toast({
              status: 'error',
              title: 'Contrato vigente ilegível',
              description: extractApiErrorMessage(errPreview),
              duration: 12000,
            });
            // aborta antes de gerar: o modal aberto la em cima sai junto,
            // senao fica por cima da tela sem nada acontecendo.
            setPreparandoGeracao(false);
            return;
          }
          const isFalhaSistema =
            statusPreview === null || statusPreview === 0 || statusPreview >= 500;
          if (isFalhaSistema) {
            toast({
              status: 'error',
              title: 'Falha ao parsear o contrato vigente',
              description: `${extractApiErrorMessage(errPreview)} Tente novamente em alguns instantes — a alteração só gera em v3 (a partir do vigente parseado).`,
              duration: 10000,
            });
            // aborta antes de gerar: o modal aberto la em cima sai junto,
            // senao fica por cima da tela sem nada acontecendo.
            setPreparandoGeracao(false);
            return;
          }
          // Alteração é v3-only (decisão 2026-06-23): qualquer falha do preview
          // (403 sem acesso, 404 sem vigente, etc.) BLOQUEIA — nunca cai em v2.
          toast({
            status: 'error',
            title:
              statusPreview === 403
                ? 'Sem acesso ao contrato vigente'
                : 'Não foi possível preparar a geração v3',
            description:
              statusPreview === 403
                ? 'Seu usuário não tem permissão pra parsear o contrato vigente (necessário pro v3). Peça ao admin pra liberar o acesso — a alteração só gera em v3.'
                : `${extractApiErrorMessage(errPreview)} A alteração só gera em v3 (a partir do vigente parseado).`,
            duration: 12000,
          });
          // aborta antes de gerar: o modal aberto la em cima sai junto,
          // senao fica por cima da tela sem nada acontecendo.
          setPreparandoGeracao(false);
          return;
        }
      }

      const payloadBody: any = {
        // Fase 6 Slice C — `tipos_alteracao` so faz sentido em alteracao.
        // Backend cruza com tipo_operacao do contrato antes de enfileirar.
        tipos_alteracao:
          values.tipo_operacao === 'alteracao' && (values.tipos_alteracao ?? []).length > 0
            ? values.tipos_alteracao
            : null,
        // Pos-Fase 6 (passo 1 do plano revisado): consolidar=true e default
        // fixo em alteracao. Sem isso, prompt builder nao adiciona o bloco
        // "MODO DA ALTERACAO: CONSOLIDATIVA" e a IA escolhe alteracao
        // simples (3 clausulas, 1593 tok output observados no contrato 54).
        // DREI prefere consolidacao — alteracao sem consolidacao e exceção
        // rara, sem toggle no MVP. Constituicao envia null (campo so faz
        // sentido em alteracao).
        consolidar: values.tipo_operacao === 'alteracao' ? true : null,
      };
      // Bloco 13 v3 alteração: patches[] + vigente_hash só em v3.
      if (engineEscolhido === 'v3' && values.tipo_operacao === 'alteracao') {
        // Quadro puro vai pro v3 sem patches de campo; o schema do endpoint
        // exige null (não lista vazia) quando não há patches.
        payloadBody.patches = patchesV3 && patchesV3.length > 0 ? patchesV3 : null;
        payloadBody.vigente_hash = vigenteHashV3;
        // Quando o quadro de sócios NÃO está sendo alterado, o quadro
        // pós-alteração == vigente. Enviamos override pra cobrir
        // vigentes com layout atípico que o `extrair_quadro_vigente`
        // regex-based do back não cobre (caso HUNTER CLUB unipessoal:
        // nome+CPF só no preâmbulo, não na cláusula do capital).
        const tiposAlt = (values.tipos_alteracao ?? []) as string[];
        // 2026-08-06: `capital` TAMBEM muda o quadro. O wizard abre o passo Socios
        // quando capital e marcado e o pre-flight EXIGE que as quotas somem o capital
        // novo — ou seja, o operador e obrigado a edita-las. Mandar `values.socios`
        // (o POS) como se fosse o vigente fazia o motor concluir que a QUANTIDADE de
        // quotas nao mudou e que subiu o VALOR da quota, e ai ele distribuia o aumento
        // proporcionalmente entre TODOS. Resultado: socio que nao pos um centavo
        // aparecia integralizando. Fecha aritmeticamente, nenhum validador barra, e o
        // documento sai errado calado. (So aparece quando a proporcao muda; com todos
        // subindo na mesma proporcao a distorcao se cancela — por isso passou batido.)
        // 2026-08-07 (review do PR #626): usa a CONSTANTE canonica em vez de listar
        // tipos a mao. `administracao` tinha ficado de fora e e' alcancavel: com so ele
        // marcado o passo Socios abre (computeSteps consome a mesma constante), o campo
        // de quotas fica livre (ninguem saindo -> nao e' destino de cessao) e o aspecto
        // 'quotas' e' oferecido em acao='alterar'. Bastava mexer numa quota pra cair na
        // MESMA classe de erro do capital: mandar o POS rotulado como vigente, fechando
        // aritmeticamente e sem validador nenhum barrando.
        if (
          !tiposAlt.some(t => (TIPOS_ALTERACAO_QUE_AFETAM_SOCIOS as readonly string[]).includes(t))
        ) {
          // O override é o quadro VIGENTE (pré-alteração): exclui quem está ENTRANDO
          // agora (acao='adicionar', ex.: admin não-sócio novo). Sem isto o back vê o
          // entrante "já no vigente" (V_SOCIO_ADICIONAR_NAO_DUPLICA_V3). Wave 2 Fase C.
          payloadBody.quadro_vigente_override = (values.socios ?? [])
            .filter(s => s.acao !== 'adicionar')
            .map(s => ({
              nome: (s.nome ?? '').trim(),
              cpf: s.cpf ?? null,
              cnpj: s.cnpj ?? null,
              quotas: Number(s.quotas ?? 0) || 0,
              tipo_vinculo: s.tipo_vinculo ?? null,
            }));
        } else {
          // GAP 2 (2026-06-10): o quadro MUDA — `values.socios` é o pós, então
          // mandamos o quadro VIGENTE do snapshot da extração do upload
          // (quadro_socios_atual) com as quotas reais. Sem isto, em vigentes
          // onde o CPF mora só no preâmbulo, o back recupera quotas=0 e
          // V_QUOTAS_TOTAL/V_REDISTRIBUICAO barram a mudança de quadro (422).
          const vig: any = createdContrato?.contrato_vigente_dados_extraidos ?? {};
          const quadroVig: any[] = Array.isArray(vig.quadro_socios_atual)
            ? vig.quadro_socios_atual
            : [];
          if (quadroVig.length > 0) {
            payloadBody.quadro_vigente_override = quadroVig.map((s: any) => ({
              nome: String(s?.nome ?? '').trim(),
              cpf: s?.cpf ?? null,
              cnpj: s?.cnpj ?? null,
              quotas: Number(s?.quotas ?? 0) || 0,
              tipo_vinculo: s?.tipo_vinculo ?? null,
            }));
          }
        }
      }

      const gerarRes = await gerarMutation.mutateAsync({
        id: contrato.id,
        engine: engineEscolhido,
        body: payloadBody,
      });
      const result = gerarRes.data;

      // A resposta chegou: a fase de PREPARO acabou em qualquer caso.
      setPreparandoGeracao(false);

      if (
        result?.status === 'gerado' ||
        result?.status === 'em_revisao' ||
        result?.status === 'campos_pendentes'
      ) {
        await releContratoGerado(contrato.id, result.status);
      }
      if (result?.status === 'gerado' || result?.status === 'em_revisao') {
        // Pos-Fase 6 (gap dos validators): `em_revisao` quando ha criticos
        // NAO-overridable. DOCX disponivel pra download mas o contrato
        // nao avanca no funnel ate alguem revisar. Same UI gating, toast
        // diferente.
        // Alteracao v3 e GATED pelo auditor: o /download responde 409 ate a
        // vistoria de fidelidade ser revisada. Nao pre-buscar aqui — evita o
        // 409 espurio no Network; o painel de vistoria re-busca via onRevisado
        // apos a confirmacao. (2026-07-15)
        const gatedAuditor = String(result?.engine ?? '').includes('alteracao');
        if (!gatedAuditor) {
          try {
            const d = await downloadContrato(contrato.id);
            setDownloadUrl(d.data?.url ?? null);
          } catch {
            setDownloadUrl(null);
          }
        }
        setGeracaoOk(true);
        // Gerado de propósito — não descartar ao sair.
        committedRef.current = true;
        if (result.status === 'em_revisao') {
          const criticos =
            (result.issues_resumo as { criticos_bloqueantes?: number } | undefined)
              ?.criticos_bloqueantes ?? 0;
          toast({
            status: 'warning',
            title: 'Contrato gerado, mas com pontos críticos',
            description: `${criticos} ${criticos === 1 ? 'item crítico' : 'itens críticos'} bloqueante${criticos === 1 ? '' : 's'}. Confira o painel de validação antes de seguir.`,
            duration: 8000,
          });
        } else {
          toast({ status: 'success', title: 'Contrato gerado com sucesso' });
        }
      } else if (result?.status === 'campos_pendentes') {
        setGeracaoOk(true);
        toast({
          status: 'info',
          title: 'Contrato gerado com pendências',
          description: 'Preencha os campos marcados pela IA para finalizar.',
        });
      } else if (result?.status === 'processing') {
        // Geração assíncrona: o acompanhamento já está aberto desde o clique e
        // permanece, consumindo `subscribeGeracaoStatus` (WS + polling, com
        // dedup no evento terminal) até o desfecho.
        setLoadingModalContratoId(contrato.id);
      } else {
        // Status desconhecido — NAO marca como OK, deixa wizard visivel.
        toast({
          status: 'warning',
          title: 'Resposta inesperada do servidor',
          description: 'Tente novamente em alguns instantes.',
        });
      }
    } catch (err: any) {
      // Qualquer erro encerra as duas fases — sem isto uma falha na validação
      // ou no envio deixaria a tela travada atrás do modal.
      setPreparandoGeracao(false);
      setLoadingModalContratoId(null);
      // Bloco 13 v3 alteração — hash-lock G-5: vigente foi re-uploadado
      // entre o operador montar os patches e clicar Gerar. Mostra dialog
      // explícito em vez de toast genérico (sessão precisa ser refeita
      // contra o vigente NOVO, não basta tentar de novo).
      const status = extractApiErrorStatus(err);
      const codigo = extractApiErrorCode(err);
      if (status === 412 && codigo === 'VIGENTE_HASH_MISMATCH') {
        showDialog({
          status: 'warning',
          title: 'Contrato vigente foi alterado',
          description:
            'O contrato vigente foi recarregado depois que você montou os ' +
            'patches desta alteração. Recarregue a página para parsear a ' +
            'versão nova do vigente e refaça os ajustes antes de gerar.',
          confirmLabel: 'Recarregar página',
          cancelLabel: 'Continuar sem recarregar',
          onConfirm: () => window.location.reload(),
          onCancel: () => undefined,
        });
        return;
      }
      if (codigo === 'FLEXAO_NAO_MAPEADA') {
        // Frente C (refator concordar 2026-06-19): erro de concordância de
        // gênero (cauda ambígua que a regra não cobre). Toast amigável e NÃO
        // bloqueante — operador corrige a profissão e gera de novo. Loading
        // reseta no finally; interface não trava.
        toast({
          status: 'warning',
          title: 'Termo de profissão não reconhecido',
          description: extractApiErrorMessage(err),
        });
        return;
      }
      const issues = extractApiErrorIssues(err);
      if (status === 422 && issues.length > 0) {
        // Gate 422 (ex.: transformação `_gate_v3_transformacao`): mostra QUAIS
        // campos faltam, não só a mensagem genérica (review #517).
        const detalhes = issues
          .map(i => [i.campo, i.mensagem || i.codigo].filter(Boolean).join(': '))
          .filter(Boolean)
          .join(' · ');
        toast({
          status: 'error',
          title: extractApiErrorMessage(
            err,
            createdContrato ? 'Falha ao regerar contrato' : 'Falha ao gerar contrato',
          ),
          description: detalhes,
          duration: 12000,
          isClosable: true,
        });
        return;
      }
      toast({
        status: 'error',
        title: createdContrato ? 'Falha ao regerar contrato' : 'Falha ao gerar contrato',
        description: extractApiErrorMessage(err),
      });
    } finally {
      // ⚠️ O reset mora AQUI, e não em cada saída, porque a classe é a saída
      // ANTECIPADA. O `return` dos críticos bloqueantes não resetava
      // `preparandoGeracao`: o operador via o toast do erro e ficava com a
      // tela de "Gerando…" por cima, dizendo "Não feche esta janela" enquanto
      // nada gerava. O único caminho de saída era o botão "Fechar e continuar
      // em background" — que informa que ele será avisado quando o contrato
      // ficar pronto (não vai, nada foi submetido) e ainda o tira do wizard.
      //
      // Nenhum dos oito resets espalhados cobria aquele `return`, e cada saída
      // nova teria que lembrar de repetir um deles. No `finally` a cobertura é
      // por construção.
      //
      // Seguro no caminho de SUCESSO: a geração assíncrona já setou
      // `loadingModalContratoId` dentro do `try`, e o modal abre com
      // `preparandoGeracao || loadingModalContratoId !== null` — o segundo
      // termo o mantém aberto.
      setPreparandoGeracao(false);
    }
  };

  // Trava o Gerar no clique, antes de qualquer await: o botão só travava depois das
  // validações, e um duplo clique rodava dois Gerar com dois PATCHs (review do PR #772).
  const gerandoRef = React.useRef(false);
  const handleGerar = async () => {
    if (gerandoRef.current) return;
    gerandoRef.current = true;
    setIsSubmitting(true);
    try {
      await gerarDoWizard();
    } finally {
      gerandoRef.current = false;
      setIsSubmitting(false);
    }
  };

  // Slice 6-D — handler do estado terminal do modal de loading.
  // O modal so dispara isso uma unica vez (dedup garantido pelo hook
  // `subscribeGeracaoStatus`). A fonte (`websocket` vs `polling`) so muda
  // quem chegou primeiro — o `data` pode estar parcial em ambos os casos
  // (WS so traz o detail, polling pode trazer `IGerarContratoResultado` ou
  // o `IContratoSocietario` se caiu no path no_task). Para garantir estado
  // consistente, refazemos GET no contrato.
  const handleLoadingTerminal = async (final: GerarContratoLoadingModalTerminal) => {
    const contratoId = loadingModalContratoId;
    setLoadingModalContratoId(null);

    if (final.status === 'falha_geracao') {
      toast({
        status: 'error',
        title: 'Falha ao gerar contrato',
        description:
          (final.data as any)?.error ||
          'A IA não conseguiu finalizar a geração. Tente regerar com ajustes.',
      });
      return;
    }
    if (final.status === 'no_task') {
      toast({
        status: 'warning',
        title: 'Geração não foi rastreada',
        description: 'A task expirou antes de retornar status. Confira a listagem de contratos.',
      });
      return;
    }
    if (contratoId == null) return;

    // Re-busca o contrato para pegar campos_pendentes/decisoes_ia/s3_key
    // — fonte unica de verdade independente de quem ganhou a corrida.
    try {
      const res = await getContrato({ queryKey: ['contrato', contratoId] } as any);
      const c = res.data;
      setCreatedContrato(c);

      if (final.status === 'gerado' || final.status === 'em_revisao') {
        // Alteracao v3 e GATED pelo auditor (download 409 ate a vistoria ser
        // revisada). Nao pre-buscar aqui — evita o 409 espurio; o painel de
        // vistoria re-busca via onRevisado apos a confirmacao. (2026-07-15)
        const gatedAuditor = c.tipo_operacao === 'alteracao' && c.engine_usado === 'v3';
        if (!gatedAuditor) {
          try {
            const d = await downloadContrato(contratoId);
            setDownloadUrl(d.data?.url ?? null);
          } catch {
            setDownloadUrl(null);
          }
        }
        setGeracaoOk(true);
        if (final.status === 'em_revisao') {
          const criticos =
            (c.issues_resumo as { criticos_bloqueantes?: number } | null | undefined)
              ?.criticos_bloqueantes ?? 0;
          toast({
            status: 'warning',
            title: 'Contrato gerado, mas com pontos críticos',
            description: `${criticos} bloqueante${criticos === 1 ? '' : 's'}. Confira o painel de validação.`,
            duration: 8000,
          });
        } else {
          toast({ status: 'success', title: 'Contrato gerado com sucesso' });
        }
      } else if (final.status === 'campos_pendentes') {
        setGeracaoOk(true);
        toast({
          status: 'info',
          title: 'Contrato gerado com pendências',
          description: 'Preencha os campos marcados pela IA para finalizar.',
        });
      }
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'Falha ao carregar contrato após geração',
        description: extractApiErrorMessage(err),
      });
    }
  };

  // O quadro lido do vigente no formato do formulário, na ordem da leitura e com quem veio
  // sem CPF/CNPJ (fora do vigentePorDoc). Null sem leitura.
  const dadosDoVigente = createdContrato?.contrato_vigente_dados_extraidos ?? null;
  const quadroLido = useMemo(
    () => (dadosDoVigente ? construirSociosDoVigente(dadosDoVigente) : null),
    [dadosDoVigente],
  );

  const renderStep = () => {
    switch (currentStep) {
      case 'tipo_operacao':
        return <StepTipoOperacao />;
      case 'verificacao_empresa':
        return <StepVerificacaoEmpresa onContinuar={handleNext} />;
      case 'contrato_vigente':
        return (
          <ContratoVigenteStep
            onUploadSuccess={handleVigenteUploadSuccess}
            dadosExtraidosPersistidos={createdContrato?.contrato_vigente_dados_extraidos ?? null}
          />
        );
      case 'tipos_alteracao':
        return <TiposAlteracaoStep ehFluxoLegalizacao={legalizacaoIdFromQuery != null} />;
      case 'empresario_individual':
        return <StepEmpresarioIndividual />;
      case 'socios': {
        // Bug 5 (smoke 2026-05-12): indexa quadro vigente por CPF/CNPJ
        // normalizado pra alimentar `quotasVigente` no AcaoSocioBlock.
        // Sem isso, a barra de progresso da redistribuicao ficava sempre
        // sem comparacao e o operador so descobria a divergencia no Gerar.
        const vigente: any = createdContrato?.contrato_vigente_dados_extraidos ?? {};
        const quadro: any[] = Array.isArray(vigente.quadro_socios_atual)
          ? vigente.quadro_socios_atual
          : [];
        const vigentePorDoc: Record<string, IVigenteDoSocio> = {};
        quadro.forEach(s => {
          const doc = String(s?.cpf || s?.cnpj || '').replace(/\D/g, '');
          // Nome e vinculo alimentam a tabela "antes -> depois" do passo Socios (25/09).
          if (doc) {
            vigentePorDoc[doc] = {
              quotas: s?.quotas ?? null,
              nome: s?.nome ?? null,
              tipo_vinculo: s?.tipo_vinculo ?? null,
            };
          }
        });
        return (
          <StepSocios
            vigentePorDoc={vigentePorDoc}
            // O quadro inteiro, inclusive quem a leitura trouxe sem CPF/CNPJ (fora do vigentePorDoc).
            quotasTotalVigente={quadro.reduce(
              (total: number, s: any) => total + (Number(s?.quotas) || 0),
              0,
            )}
            quadroLido={quadroLido}
          />
        );
      }
      case 'administracao':
        // `key` por assunto: as duas etapas usam o mesmo componente no mesmo lugar; sem ela, ir de uma
        // para a outra reaproveitava a conversa e a redação da anterior (review de 15/10).
        return (
          <StepAdministracao
            key="administracao"
            contratoId={createdContrato?.id ?? null}
            estadoInicial={createdContrato?.administracao_ia}
            vigente={createdContrato?.contrato_vigente_dados_extraidos}
            onEstado={novo =>
              setCreatedContrato(atual => (atual ? { ...atual, administracao_ia: novo } : atual))
            }
            onEdicaoPendente={setAdministracaoEditadaNaTela}
            onMudarQuemAdministra={() => irParaEtapa('socios')}
          />
        );
      case 'clausulas':
        return (
          <StepAdministracao
            key="clausulas"
            assunto="clausulas"
            contratoId={createdContrato?.id ?? null}
            estadoInicial={createdContrato?.clausulas_ia}
            vigente={createdContrato?.contrato_vigente_dados_extraidos}
            onEstado={novo =>
              setCreatedContrato(atual => (atual ? { ...atual, clausulas_ia: novo } : atual))
            }
            onEdicaoPendente={setClausulasEditadasNaTela}
          />
        );
      case 'empresa': {
        // Sessao 2026-05-11 (itens 21 Fase 1 + 18 Fase 2): passa dados
        // extraidos do vigente pro StepEmpresa renderizar contextualmente
        // (helper numero_alteracao + capital "De/Para").
        const vigente: any = createdContrato?.contrato_vigente_dados_extraidos ?? {};
        return (
          <StepEmpresa
            numeroUltimaAlteracao={vigente.numero_ultima_alteracao ?? null}
            capitalSocialVigente={vigente.capital_social_atual ?? null}
            quotasTotalVigente={
              (vigente.quadro_socios_atual ?? []).reduce(
                (t: number, s: any) => t + (Number(s?.quotas) || 0),
                0,
              ) || null
            }
            cnaesSugeridos={cnaesForamSugeridos}
            atuais={atuaisDoVigente(vigente)}
          />
        );
      }
      case 'revisao':
      default: {
        // Instrução para a IA REMOVIDA (2026-07-15): o v3 é determinístico e a
        // ignora; só o v1/v2 (IA-first, em sunset) a consumiam. O step Revisão
        // ocupa a largura completa e concentra o "o que será alterado".
        // Passa o snapshot do vigente pra o resumo mostrar antes → depois.
        const vigenteRevisao: any = createdContrato?.contrato_vigente_dados_extraidos ?? {};
        return (
          <StepRevisao
            vigente={vigenteRevisao}
            clausulasAprovadas={createdContrato?.clausulas_ia?.aprovado}
          />
        );
      }
    }
  };

  // Estado "pós-geração" — so renderiza quando a geracao retornou OK.
  // `createdContrato` sozinho nao basta porque pode ter sido setado em retry
  // idempotente (o create deu OK mas o gerar falhou — UI nao deve celebrar).
  if (createdContrato && geracaoOk) {
    return (
      <PageBox>
        <Box minH="100vh" bg="gray.50">
          <CabecalhoDoWizard
            contrato={createdContrato}
            onSair={handleSairDoWizard}
            complemento="Geração concluída. Siga para o próximo passo abaixo."
          />
          <Box px={{ base: 4, md: 8 }} py={6}>
            <Box maxW="1280px" mx="auto">
              <Box mt={4}>
                <GlassCard intensity="strong" p={5} borderColor="rgba(56, 161, 105, 0.4)">
                  <Alert
                    status="success"
                    flexDirection="column"
                    alignItems="flex-start"
                    bg="transparent"
                    p={0}
                  >
                    <Flex align="center" gap={2}>
                      <AlertIcon />
                      <AlertTitle>Contrato pronto!</AlertTitle>
                    </Flex>
                    <AlertDescription mt={2}>{mensagemDocumentoPronto}</AlertDescription>
                    <Flex gap={3} mt={3} flexWrap="wrap">
                      {downloadUrl && (
                        <Button
                          colorScheme={temRevisada ? 'green' : 'blue'}
                          onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}
                        >
                          {temRevisada ? 'Baixar versão revisada' : 'Baixar DOCX'}
                        </Button>
                      )}
                      {/* O original só aparece DEPOIS de existir revisada — antes
                      disso ele É o documento, e um segundo botão idêntico só
                      confundiria. */}
                      {temRevisada && urlOriginal && (
                        <Button
                          variant="outline"
                          onClick={() => window.open(urlOriginal, '_blank', 'noopener,noreferrer')}
                        >
                          Baixar original gerado
                        </Button>
                      )}
                      {/* Reenvio da versão ajustada à mão. Só aparece depois que o
                      download liberou (mesmo gate): não se revisa o que não se
                      conseguiu baixar. Em alteração apenas — constituição e
                      transformação não têm o par vigente × render que dá sentido
                      à comparação. */}
                      {downloadUrl && createdContrato.tipo_operacao === 'alteracao' && (
                        <>
                          <Button
                            variant="outline"
                            colorScheme="green"
                            leftIcon={<FileArrowUp size={16} weight="bold" />}
                            onClick={() => inputRevisadoPosGeracao.current?.click()}
                          >
                            {temRevisada ? 'Substituir versão revisada' : 'Enviar versão revisada'}
                          </Button>
                          <input
                            ref={inputRevisadoPosGeracao}
                            type="file"
                            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            style={{ display: 'none' }}
                            onChange={async e => {
                              const file = e.target.files?.[0];
                              // Zera: reenviar o MESMO arquivo depois de corrigi-lo
                              // não dispara onChange sem isto.
                              e.target.value = '';
                              if (!file) return;
                              try {
                                await uploadDocumentoEditado({
                                  contratoId: createdContrato.id,
                                  file,
                                });
                                // A URL assinada em `downloadUrl` aponta pro DOCX
                                // gerado. Depois do envio ela esta' obsoleta: o
                                // documento do ato passou a ser outro. Refaz as
                                // duas — a principal (revisada) e a do original.
                                try {
                                  const dNovo = await downloadContrato(createdContrato.id);
                                  setDownloadUrl(dNovo.data?.url ?? null);
                                } catch {
                                  setDownloadUrl(null);
                                }
                                try {
                                  const dOrig = await downloadContrato(createdContrato.id, {
                                    original: true,
                                  });
                                  setUrlOriginal(dOrig.data?.url ?? null);
                                } catch {
                                  setUrlOriginal(null);
                                }
                                setTemRevisada(true);
                                toast({
                                  status: 'success',
                                  title: 'Versão revisada anexada',
                                  description:
                                    'Ela passa a ser o documento deste ato. O original ' +
                                    'gerado continua disponível aqui e na listagem.',
                                });
                              } catch (err: any) {
                                toast({
                                  status: 'error',
                                  title: 'Falha ao enviar a versão revisada',
                                  description: extractApiErrorMessage(err),
                                });
                              }
                            }}
                          />
                        </>
                      )}
                      <Button variant="outline" onClick={() => navigate('/societario/contratos')}>
                        Ir para a listagem
                      </Button>
                    </Flex>
                  </Alert>
                </GlassCard>
              </Box>

              <Box mt={4}>
                <IssuesPanel
                  contratoId={createdContrato.id}
                  engine={createdContrato.engine_usado ?? null}
                />
              </Box>

              {/* Vistoria (Relatório de Fidelidade) — Fase 1 GATE OBRIGATÓRIO. O
              limitador de grupo CAIU (decisão 2026-07-03): TODOS que geram a
              alteração veem e confirmam a vistoria (o endpoint por-contrato passou a
              escopar por _assert_acesso_contrato, não _require_auditoria). Só
              alteração dispara auditor → só ela aparece aqui. NÃO gateia por
              engine_usado (createdContrato stale logo após gerar); o painel polla o
              relatório assíncrono. `onRevisado` re-busca o download (o back destranca
              o 409) e habilita o "Baixar DOCX". */}
              {createdContrato.tipo_operacao === 'alteracao' && (
                <Box mt={4}>
                  <ValidacaoFidelidadePosGeracao
                    contratoId={createdContrato.id}
                    onRevisado={async () => {
                      try {
                        const d = await downloadContrato(createdContrato.id);
                        setDownloadUrl(d.data?.url ?? null);
                      } catch {
                        setDownloadUrl(null);
                      }
                    }}
                  />
                </Box>
              )}

              <GerarContratoLoadingModal
                isOpen={preparandoGeracao || loadingModalContratoId !== null}
                contratoId={loadingModalContratoId}
                onTerminal={handleLoadingTerminal}
                onError={() => undefined}
                onDismiss={
                  loadingModalContratoId === null
                    ? undefined
                    : () => {
                        setLoadingModalContratoId(null);
                        toast({
                          status: 'info',
                          title: 'Geração em segundo plano',
                          description: 'Você será notificado quando o contrato estiver pronto.',
                          duration: 5000,
                        });
                        navigate('/societario/contratos');
                      }
                }
              />
            </Box>
          </Box>
        </Box>
      </PageBox>
    );
  }

  // Estado do salvamento mostrado na barra (antes ficava no topo, e só a falha).
  let estadoDoSalvamento: EstadoDoSalvamento = 'nunca';
  if (salvando) estadoDoSalvamento = 'salvando';
  else if (autoSaveFalhou) estadoDoSalvamento = 'erro';
  else if (salvoEm) estadoDoSalvamento = 'salvo';

  return (
    <PageBox>
      <FormProvider {...formMethods}>
        <Box minH="100vh" bg="gray.50">
          <CabecalhoComTituloDoForm
            contrato={createdContrato}
            tipoOperacao={tipoOperacaoWatched}
            onSair={handleSairDoWizard}
          />

          {/* pb: a barra fixa do rodapé não pode cobrir o fim da etapa. */}
          <Box px={{ base: 4, md: 8 }} pt={2} pb="150px">
            <Box maxW="1280px" mx="auto" ref={colunaRef}>
              <Box ref={topoDasEtapasRef} style={{ scrollMarginTop: TOPO_ABAIXO_DO_CABECALHO }}>
                <WizardStepper
                  steps={stepsList}
                  currentStep={currentStep}
                  alcancadoIdx={alcancadoIdx}
                  onStepClick={stepKey => {
                    irParaEtapa(stepKey as StepKey);
                  }}
                />
              </Box>

              <GlassCard intensity="strong" p={5} mt={2}>
                {renderStep()}
              </GlassCard>

              {/* Só na Revisão, junto do Gerar: nas outras etapas o aviso não servia. */}
              {currentStep === 'revisao' && (
                <Text fontSize="xs" color="gray.500" mt={3}>
                  {ehFluxoConstituinte(tipoOperacaoWatched)
                    ? 'A geração usa o template determinístico e pode demorar alguns segundos. Não feche a página.'
                    : 'A geração usa IA + template e pode demorar alguns segundos. Não feche a página.'}
                </Text>
              )}
            </Box>
          </Box>

          <BarraDoWizard
            colunaRef={colunaRef}
            salvamento={estadoDoSalvamento}
            salvoEm={salvoEm}
            onTentarSalvar={() => {
              persistirRascunho(true).catch(() => undefined);
            }}
            podeVoltar={currentIdx > 0}
            onEtapaAnterior={handleBack}
            onSalvarESair={handleSalvarESair}
            salvandoESaindo={isSavingDraft || retomandoEdicao}
          >
            {currentStep !== 'revisao' ? (
              <Button
                colorScheme="blue"
                rightIcon={<ArrowRight size={16} weight="bold" />}
                onClick={handleNext}
                isLoading={updateMutation.isPending}
              >
                Continuar
              </Button>
            ) : (
              <>
                {/* Sessao 2026-05-11 (item 26 Fase 5): preview deterministico
                    ao lado do Gerar. Custo zero (sem IA). */}
                <PreviewDeterministicoModal
                  contratoId={createdContrato?.id ?? null}
                  montarBodyAlteracao={montarBodyPreviewAlteracao}
                />
                <BotaoGerarContrato
                  exigeAdmin={exigeAdmin}
                  isLoading={isSubmitting}
                  onClick={handleGerar}
                />
              </>
            )}
          </BarraDoWizard>

          <GerarContratoLoadingModal
            isOpen={preparandoGeracao || loadingModalContratoId !== null}
            contratoId={loadingModalContratoId}
            onTerminal={handleLoadingTerminal}
            onError={() => undefined}
            onDismiss={() => {
              // Sessao 2026-05-08 (Fase C): operador escolhe continuar em
              // background durante a geracao. Subscription WS continua ativa
              // no NotificationProvider (global) — toast + persistencia em
              // tb_notificacoes acontecem quando IA termina.
              setLoadingModalContratoId(null);
              toast({
                status: 'info',
                title: 'Geração em segundo plano',
                description: 'Você será notificado quando o contrato estiver pronto.',
                duration: 5000,
              });
              navigate('/societario/contratos');
            }}
          />
        </Box>
      </FormProvider>
    </PageBox>
  );
};

export default SocietarioContratoCriarPage;
